import { CardDef, DeckDef, GameState, GameAction, GameEvent, Phase } from './types.js';
import { getConfig } from './config.js';
import { createInitialState, dispatch, getPlayerState, getInstance, getOpponentSide, getEffectivePower } from './engine/GameState.js';
import { generateSeed } from './engine/PRNG.js';
import { type GameRecord, hashState } from './engine/GameRecord.js';
import { advancePhase, advanceUntilInteractive, hasEligibleAttackers, skipToCounterPhase } from './engine/TurnManager.js';
import { resolveCombat } from './engine/CombatResolver.js';
import { getLegalActions, canBlockAnyAttacker, hasPlayableReaction } from './engine/Rules.js';
import { getMaxHandSize } from './engine/AbilityProcessor.js';
import { getAIAction } from './ai/BasicAI.js';
import { searchActionInWorker } from './ai/search-client.js';
import { thinkingStart, thinkingEnd, thinkingReset } from './ui/ThinkingIndicator.js';
import type { AIProfile, SearchReport } from './ai/types.js';
import { projectForSide } from './engine/Perspective.js';
import { mountAIDebugPanel } from './ui/AIDebugPanel.js';
import { Renderer, type RewardPreview } from './ui/Renderer.js';
import {
  loadPlayerData, getActiveDeck,
  initFromStarterDeck, buyPack, reportGame, claimDailyGems,
  saveDeck, deleteDeck, setActiveDeck, registerAccount, signIn, signOut,
  mergeCustomCardsIntoCatalog, mergeMissingCardsByIds, refreshAuctionSummary,
  checkDeckPlayable,
  PlayerData, GameReportMode,
} from './collection/PlayerState.js';
import type { PackDef, PacksConfig, StoreConfig, DeckSlotCostsConfig } from './shared/api-types.js';
import type { GameRule, DeckRule, RulesBundle } from './shared/rules.js';
import { validateDeckAgainstRule, resolveRule } from './shared/rules.js';
import type { CustomPackConfig } from './shared/cardgen.js';
import { createStarterSelectionView, createMainMenuView, MenuAction } from './ui/MenuView.js';
import { createCampaignListView, getCampaignState } from './ui/CampaignListView.js';
import { createCampaignStagesView } from './ui/CampaignStagesView.js';
import { createTournamentListView } from './ui/TournamentListView.js';
import { createTournamentDetailView } from './ui/TournamentDetailView.js';
import { createLadderView } from './ui/LadderView.js';
import { createSettingsView } from './ui/SettingsView.js';
import type { TournamentSummary, TournamentPreview } from './shared/tournament-types.js';
import { createDeckBuilderView } from './ui/DeckBuilderView.js';
import { createPackOpeningView } from './ui/PackOpeningView.js';
import { showCoinFlip } from './ui/CoinFlipView.js';
import { showStageIntro } from './ui/StageIntroView.js';
import { showNotice, showConfirm, showPrompt } from './ui/NoticeView.js';
import { showEndGameInterstitial } from './ui/EndGameInterstitial.js';
import { showVoiceBubble } from './ui/VoiceBubble.js';
import { pickVoiceLine, logVoiceLine } from './ui/VoiceLinePool.js';
import { prefetchDossiers, refreshDossierCache, lookupDossierOrNull } from './collection/DossierCache.js';
import { resolveTournamentDeckIndex, setTournamentDeckId } from './collection/TournamentDeckPrefs.js';
import { createCustomPackView } from './ui/CustomPackView.js';
import { showStore } from './ui/GemShopView.js';
import { hideTooltip, createCardElement } from './ui/CardView.js';
import { playActionCardCeremony, playCharacterAbilityBanner, playSupportCardCeremony } from './ui/Ceremony.js';
import { spawnGemGainFlyaway } from './ui/Flyaway.js';
import { ticketDisplayName } from './shared/tickets.js';
import { CUSTOM_PACK_TICKET_ID } from './shared/cardgen.js';
import { apiClient } from './api-client.js';
import { migrateCard } from './shared/card-migrations.js';

interface DifficultyBucket {
  upTo: number | null;
  weights: Record<string, number>;
}
interface DifficultyConfig {
  buckets: DifficultyBucket[];
}
type DeckManifest = Record<string, string[]>;

let catalog: Map<string, CardDef>;
let state: GameState;
let renderer: Renderer;
let playerData: PlayerData;
let packsConfig: PacksConfig;
let storeConfig: StoreConfig;
let deckSlotCosts: DeckSlotCostsConfig;
let customPackConfig: CustomPackConfig;
let starterDecks: DeckDef[];
let decksByTier: Record<string, DeckDef[]> = {};
let aiDifficulty: DifficultyConfig;
let rulesBundle: RulesBundle;
// Decks referenced by `stage.opponentDeck` in any campaign — preloaded at
// init so startGame() stays sync. Keyed by the filename (without .json).
let opponentDecksByFile: Record<string, DeckDef> = {};
let container: HTMLElement;
let actionLog: GameAction[] = [];
/** Live replayable record for the current match. Populated at startGame()
 *  with the seed + decks + rule; the `actions` array points to the same
 *  underlying `actionLog`. Finalized at game end (sets `finalStateHash` +
 *  `recordedAt`). Exposed to the browser console via
 *  `window.__cardverseExportRecord()` for grabbing during dev/replay
 *  testing. Null between matches. */
let currentGameRecord: GameRecord | null = null;

/** Active SearchAI tuning profile (data/ai-tuning/<id>.json). Resolved
 *  per-match from data/ai-tuning/_assignments.json based on the active
 *  game mode (and skirmish AI deck tier). `?ai-search=<id>` URL param
 *  overrides assignment-based selection for testing. null = BasicAI is
 *  in the live path. */
let activeSearchProfile: AIProfile | null = null;
/** All loaded profile files, keyed by id. Populated at init. */
const aiProfilesById = new Map<string, AIProfile>();
/** Game-mode → profile-id lookup loaded from _assignments.json. Strings
 *  may also be 'basic' to force BasicAI for that mode. Keys follow the
 *  precedence rules documented in _assignments.json. */
let aiAssignments: Record<string, string> = {};
/** Set by `?ai-search=<id>` (or `?ai-search=basic`) URL flag at init.
 *  When non-undefined, overrides the per-mode assignment lookup —
 *  useful for dev to force a profile across all matches. The string
 *  'basic' forces BasicAI; any other string is treated as a profile id. */
let aiSearchUrlOverride: string | undefined;
/** Handle returned by mountAIDebugPanel — null when the panel isn't
 *  mounted (production builds without ?ai-debug=1). */
let aiDebugPanel: { update: (report: SearchReport | null) => void } | null = null;

/** Single choke point for AI move selection. Routes to SearchAI (run in a
 *  Web Worker via search-client, so a long think never blocks the UI) when a
 *  profile is active for the current match, else BasicAI on a projected
 *  (perspective-filtered) state. The chosen action is dispatched against the
 *  LIVE (full-info) state by the caller. SearchAI handles its own
 *  determinization internally per profile. The thinking* calls drive the
 *  "Thinking…" indicator, which appears only if the AI takes a while. */
async function pickOpponentAction(): Promise<import('./types.js').GameAction | null> {
  if (activeSearchProfile) {
    thinkingStart();
    try {
      const { action, report } = await searchActionInWorker(
        state, catalog, 'opponent', activeSearchProfile,
      );
      aiDebugPanel?.update(report);
      return action;
    } finally {
      thinkingEnd();
    }
  }
  return getAIAction(projectForSide(state, 'opponent'), catalog, 'opponent');
}

/** Pick the AI profile for a match based on game mode + the AI's deck
 *  filename (for skirmish tiering). Returns null for BasicAI. */
function resolveProfileForMatch(mode: GameReportMode, aiDeckName: string): AIProfile | null {
  // URL override wins everything (dev flag).
  if (aiSearchUrlOverride !== undefined) {
    if (aiSearchUrlOverride === 'basic') return null;
    return aiProfilesById.get(aiSearchUrlOverride) ?? null;
  }
  // Per-match lookup keys in precedence order.
  const keys: string[] = [];
  switch (mode.type) {
    case 'campaign':
      keys.push(`campaign.${mode.campaignId}`);
      keys.push('campaign');
      break;
    case 'skirmish': {
      // Two-digit tier prefix on the deck filename (e.g. "01-..." → "01").
      const m = /^(\d{2})/.exec(aiDeckName);
      if (m) keys.push(`skirmish.${m[1]}`);
      keys.push('skirmish');
      break;
    }
    case 'tournament':
      keys.push('tournament');
      break;
    case 'practice':
      keys.push('practice');
      break;
  }
  for (const key of keys) {
    const profileId = aiAssignments[key];
    if (profileId === undefined) continue;
    if (profileId === 'basic') return null;
    const profile = aiProfilesById.get(profileId);
    if (profile) return profile;
    console.warn(`[ai] profile '${profileId}' referenced from _assignments.json:${key} but not loaded; falling back`);
  }
  // No assignment matched — default to BasicAI.
  return null;
}

/** Fetch an AI profile JSON and all its $extends ancestors into `rawById`
 *  (keyed by id), so resolveRule can merge the inheritance chain. The
 *  client can't list the directory, so parents are discovered by following
 *  the $extends link. Idempotent per id. */
async function fetchProfileChain(id: string, rawById: Map<string, unknown>): Promise<void> {
  if (rawById.has(id)) return;
  const r = await fetch(`data/ai-tuning/${id}.json`);
  if (!r.ok) { console.warn(`[ai] profile '${id}' failed to load`); return; }
  const raw = await r.json() as { $extends?: string | null };
  rawById.set(id, raw);
  if (raw.$extends) await fetchProfileChain(raw.$extends, rawById);
}

/** Fetch + $extends-resolve the given profile ids into `aiProfilesById`. */
async function loadAndResolveProfiles(ids: string[]): Promise<void> {
  const rawById = new Map<string, unknown>();
  await Promise.all(ids.map(id => fetchProfileChain(id, rawById)));
  const rawObj = Object.fromEntries(rawById) as Record<string, unknown>;
  for (const id of ids) {
    if (!rawById.has(id)) continue;
    try {
      aiProfilesById.set(id, resolveRule<AIProfile>(rawObj, id, 'ai-profile'));
    } catch (e) {
      console.warn(`[ai] failed to resolve profile '${id}':`, e);
    }
  }
}

/** Walk the recorded action log and return the unique set of cardIds
 *  the player actually played this match — characters, actions,
 *  reactions, and the hand cards consumed for supports. Excludes the
 *  opponent's plays since they belong to a different player's
 *  collection. Sent in the game report so the server can bump the
 *  player's affinity for those cards.
 *
 *  Resolves cardId via `state.cardInstances` since GameAction entries
 *  carry only instance IDs. State must still be alive at the call
 *  site (i.e. before any teardown). */
function collectPlayerCardsPlayed(): string[] {
  if (!state) return [];
  const set = new Set<string>();
  const visit = (instanceId: string): void => {
    const inst = state.cardInstances.get(instanceId);
    if (!inst) return;
    if (inst.owner !== 'player') return;
    set.add(inst.cardId);
  };
  for (const a of actionLog) {
    switch (a.type) {
      case 'PLAY_CHARACTER':
      case 'PLAY_ACTION':
      case 'PLAY_REACTION':
        visit(a.instanceId);
        break;
      case 'SUPPORT_CHARACTER':
        // The hand card is the speaker / consumed card; the ring card
        // is just the target. Affinity goes to the one that was
        // discarded from hand.
        visit(a.handInstanceId);
        break;
    }
  }
  return Array.from(set);
}
// Mode of the in-flight game — startGame() sets this; onGameOver() reads it
// so the report carries the right campaign/stage context.
let activeGameMode: GameReportMode = { type: 'skirmish' };
// Captured at startGame() for tournament reporting: the player's deck used
// in this match (snapshotted to the server on report) and the start-rank
// returned by /start-match (so the game-over modal can show the delta even
// after the player's rank may have shifted server-side).
let activeMatchDeck: DeckDef | null = null;
let activeMatchStartRank: number | null = null;
// ── Voice-line / interstitial state (Phase 2 dossier display) ──────
// Profile-card identities for this match — resolved at startMatch and
// stored module-wide so the Renderer (header icons + surprised/gloat
// bubbles), the end-game interstitial, and any future codex view can
// all read from one place. Cleared on each new match.
let activeOpponentProfileCardId: string | null = null;
let activePlayerProfileCardId: string | null = null;
// Captured from the `game-over` event when it fires so the end-game
// interstitial can show the right card sliding in for kill_opponent.
// Null for deck-out (the interstitial synthesizes a placeholder).
let pendingEndgameResponsibleCardId: string | null = null;
// Cleanup function for the currently-mounted tournament view (cancels
// countdown timers / observers). Called on next navigate / renderRoute.
let activeTournamentViewCleanup: (() => void) | null = null;

// ── Card & Data Loading ──────────────────────────────────────────

async function loadCardCatalog(): Promise<Map<string, CardDef>> {
  const response = await fetch('data/cards/_index.json');
  const cardIds: string[] = await response.json();

  const catalog = new Map<string, CardDef>();
  const promises = cardIds.map(async (id) => {
    const resp = await fetch(`data/cards/${id}.json`);
    const card = migrateCard(await resp.json());
    catalog.set(card.id, card);
  });
  await Promise.all(promises);
  return catalog;
}

async function loadDeck(path: string): Promise<DeckDef> {
  const response = await fetch(path);
  return response.json();
}

async function loadPacksConfig(): Promise<PacksConfig> {
  const resp = await fetch('data/packs.json');
  return resp.json() as Promise<PacksConfig>;
}

async function loadCustomPackConfig(): Promise<CustomPackConfig> {
  const resp = await fetch('data/custom-pack.json');
  return resp.json() as Promise<CustomPackConfig>;
}

async function loadDeckSlotCosts(): Promise<DeckSlotCostsConfig> {
  const resp = await fetch('data/deck-slot-costs.json');
  return resp.json() as Promise<DeckSlotCostsConfig>;
}

async function loadStoreConfig(): Promise<StoreConfig> {
  // Static config fetched at boot. Could move to
  // api-client.getStoreConfig() (which proxies `/api/gem-shop/offers`)
  // if we ever want server-side per-user pricing or A/B variants;
  // for now the public-static path is fine and avoids a request on
  // the critical boot path.
  const resp = await fetch('data/gem-shop.json');
  return resp.json() as Promise<StoreConfig>;
}

async function loadDeckManifest(): Promise<DeckManifest> {
  const resp = await fetch('data/decks/_index.json');
  return resp.json() as Promise<DeckManifest>;
}

async function loadDifficultyConfig(): Promise<DifficultyConfig> {
  const resp = await fetch('data/ai-difficulty.json');
  return resp.json() as Promise<DifficultyConfig>;
}

/** Decide which mode the next "Play" press should run. The intro campaign is
 *  the player's onboarding battle and runs whenever hasReceivedStarterPack is
 *  false (whether they haven't played yet, or lost the intro and haven't
 *  retried). After it resolves once, every subsequent Play is a skirmish. */
function chooseGameMode(): GameReportMode {
  if (!playerData.hasReceivedStarterPack) {
    const intro = rulesBundle.campaigns['intro'];
    if (intro && intro.stages.length > 0) {
      return { type: 'campaign', campaignId: intro.id, stageId: intro.stages[0].id };
    }
  }
  return { type: 'skirmish' };
}

async function loadCampaignOpponentDecks(rules: RulesBundle): Promise<Record<string, DeckDef>> {
  const filenames = new Set<string>();
  for (const camp of Object.values(rules.campaigns)) {
    for (const stage of camp.stages) {
      if (stage.opponentDeck) filenames.add(stage.opponentDeck);
    }
  }
  const out: Record<string, DeckDef> = {};
  await Promise.all([...filenames].map(async name => {
    out[name] = await loadDeck(`data/decks/${name}.json`);
  }));
  return out;
}

function resolveGameModeRules(mode: GameReportMode): { gameRule: GameRule; deckRule: DeckRule } {
  if (mode.type === 'tournament') {
    // Rule ids were snapshotted at click time in startTournamentMatch
    // and carried on the mode itself. This used to look them up in
    // tournamentCache every call — but the cache can be refreshed
    // (or reduced by a pattern-skip) between click and match-start,
    // and a missing entry silently fell back to skirmish defaults,
    // so a Pauper League tap could end up playing a 3000-HP match
    // with an unrestricted deck. Reading straight off the mode
    // eliminates that race entirely.
    return {
      gameRule: rulesBundle.gameRules[mode.gameRuleId] ?? rulesBundle.gameRules[rulesBundle.skirmish.gameRule],
      deckRule: rulesBundle.deckRules[mode.deckRuleId] ?? rulesBundle.deckRules[rulesBundle.skirmish.deckRule],
    };
  }
  if (mode.type === 'skirmish' || mode.type === 'practice') {
    // Practice reuses skirmish rules unconditionally. Skirmish itself
    // reads from the rules bundle's `skirmish` defaults — HP, hand,
    // deck size all standard.
    return {
      gameRule: rulesBundle.gameRules[rulesBundle.skirmish.gameRule],
      deckRule: rulesBundle.deckRules[rulesBundle.skirmish.deckRule],
    };
  }
  const camp = rulesBundle.campaigns[mode.campaignId];
  const stage = camp.stages.find(s => s.id === mode.stageId)!;
  return {
    gameRule: rulesBundle.gameRules[stage.gameRule ?? camp.gameRule],
    deckRule: rulesBundle.deckRules[stage.deckRule ?? camp.deckRule],
  };
}

async function loadAllDecks(manifest: DeckManifest): Promise<Record<string, DeckDef[]>> {
  const tiers: Record<string, DeckDef[]> = {};
  await Promise.all(
    Object.entries(manifest).map(async ([tier, files]) => {
      tiers[tier] = await Promise.all(files.map(f => loadDeck(`data/decks/${f}`)));
    })
  );
  return tiers;
}

function weightedPick<K extends string>(weights: Record<K, number>): K {
  const entries = Object.entries(weights) as [K, number][];
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = Math.random() * total;
  for (const [key, w] of entries) {
    r -= w;
    if (r <= 0) return key;
  }
  return entries[entries.length - 1][0];
}

function pickAiDeck(score: number, excludeName: string): DeckDef {
  const bucket = aiDifficulty.buckets.find(b => b.upTo === null || score <= b.upTo)
    ?? aiDifficulty.buckets[aiDifficulty.buckets.length - 1];
  const tier = weightedPick(bucket.weights);
  const candidates = (decksByTier[tier] ?? []).filter(d => d.name !== excludeName);
  if (candidates.length > 0) {
    return candidates[Math.floor(Math.random() * candidates.length)];
  }
  // Fall back to any tier 00 deck that isn't the player's
  const fallback = (decksByTier['00'] ?? []).filter(d => d.name !== excludeName);
  return fallback[Math.floor(Math.random() * fallback.length)] ?? starterDecks[0];
}

// ── Screen Navigation ────────────────────────────────────────────

function showStarterSelection(): void {
  createStarterSelectionView(container, starterDecks, catalog, handleMenuAction, playerData.isGuest);
}

// Cached tournament summaries — fetched lazily on each main-menu render
// and reused until the next fetch lands. The menu renders eagerly with
// whatever's cached (possibly nothing on first paint); when the fetch
// resolves with a different set, we re-render. Cache is intentionally
// per-session — short-lived data the player wants fresh.
let tournamentCache: TournamentSummary[] = [];
let tournamentPreviewCache: TournamentPreview[] = [];
let tournamentFetchInFlight = false;

/** Pip on the Campaign button: true when at least one non-hidden
 *  campaign is currently AVAILABLE (unlocked but not fully cleared).
 *  Reuses getCampaignState so "incomplete" matches what the campaign
 *  list shows the player — locked (prereqs unmet) and fully-completed
 *  campaigns don't trigger the nudge. */
function campaignHasIncompleteStages(): boolean {
  return Object.values(rulesBundle.campaigns).some(
    c => !c.hidden && getCampaignState(c, playerData.beatenStageIds) === 'available',
  );
}

/** Tournament gate (data/progression.json): unlocked once every
 *  required stage id is in the player's beaten set. */
function tournamentsUnlocked(): boolean {
  const beaten = new Set(playerData.beatenStageIds);
  return rulesBundle.progression.tournamentUnlockStageIds.every(id => beaten.has(id));
}

/** Auction House gate (data/progression.json): same pattern as tournaments —
 *  unlocked once the player has cleared every stage id in
 *  progression.auctionHouseUnlockStageIds (currently the final Caesar
 *  stage). Signed-in-only requirement is enforced separately by the
 *  route handlers; this helper only reports the campaign-progression
 *  side of the gate. */
export function auctionHouseUnlocked(): boolean {
  const gate = rulesBundle.progression.auctionHouseUnlockStageIds ?? [];
  if (gate.length === 0) return true;
  const beaten = new Set(playerData.beatenStageIds);
  return gate.every(id => beaten.has(id));
}

function showMainMenu(): void {
  hideTooltip();
  thinkingReset();  // never let the AI "Thinking…" overlay linger onto the menu
  createMainMenuView(container, playerData, catalog, packsConfig.packs, getConfig().maxDecks, handleMenuAction, playerData.isGuest, playerData.username, customPackConfig, tournamentCache, campaignHasIncompleteStages(), tournamentsUnlocked(), deckSlotCosts.costs, auctionHouseUnlocked(), playerData.auctionSummary);
  // Background-refresh tournaments. Guests see the tournament card too
  // so they understand the feature exists; clicking Join just pops the
  // account-gate modal (see startTournamentMatch).
  if (!tournamentFetchInFlight) {
    tournamentFetchInFlight = true;
    apiClient.getTournaments().then(({ tournaments, previews }) => {
      tournamentFetchInFlight = false;
      const changed = JSON.stringify(tournaments) !== JSON.stringify(tournamentCache);
      tournamentCache = tournaments;
      tournamentPreviewCache = previews;
      // Only re-render if we're still on the menu AND the data
      // actually changed — avoids a redundant DOM blow-away.
      if (changed && parseRoute(location.pathname).kind === 'home') {
        createMainMenuView(container, playerData, catalog, packsConfig.packs, getConfig().maxDecks, handleMenuAction, playerData.isGuest, playerData.username, customPackConfig, tournamentCache, campaignHasIncompleteStages(), tournamentsUnlocked(), deckSlotCosts.costs, auctionHouseUnlocked(), playerData.auctionSummary);
      }
    }).catch(err => {
      tournamentFetchInFlight = false;
      console.warn('Failed to refresh tournaments', err);
    });
  }
  // Background-refresh the auction summary so pips + counts update
  // between visits. Signed-in only (see refreshAuctionSummary).
  if (auctionHouseUnlocked() && !playerData.isGuest) {
    const beforeJson = JSON.stringify(playerData.auctionSummary);
    void refreshAuctionSummary(playerData).then(() => {
      const afterJson = JSON.stringify(playerData.auctionSummary);
      if (beforeJson !== afterJson && parseRoute(location.pathname).kind === 'home') {
        createMainMenuView(container, playerData, catalog, packsConfig.packs, getConfig().maxDecks, handleMenuAction, playerData.isGuest, playerData.username, customPackConfig, tournamentCache, campaignHasIncompleteStages(), tournamentsUnlocked(), deckSlotCosts.costs, auctionHouseUnlocked(), playerData.auctionSummary);
      }
    });
  }
}

function showSettings(): void {
  hideTooltip();
  // Settings carousel does its own intersection-observer lifecycle;
  // the teardown function is stashed in `activeTournamentViewCleanup`
  // since both flows are mutually exclusive (a route render tears
  // down whichever was active). Renaming the global would touch
  // several call sites; reuse it for now and rename later if a third
  // long-lived overlay shows up.
  activeTournamentViewCleanup = createSettingsView(container, playerData, catalog, handleMenuAction);
}

async function showTournamentList(): Promise<void> {
  hideTooltip();
  try {
    const { tournaments, previews } = await apiClient.getTournaments();
    tournamentCache = tournaments;
    tournamentPreviewCache = previews;
    activeTournamentViewCleanup = createTournamentListView(container, tournaments, previews, playerData, handleMenuAction);
  } catch (err) {
    console.error('Failed to load tournaments', err);
    showMainMenu();
  }
}

async function showTournamentDetail(tournamentId: string): Promise<void> {
  hideTooltip();
  try {
    const detail = await apiClient.getTournamentDetail(tournamentId);
    // Resolve any other-player profile cards (custom cards not in the
    // local catalog) before render so the avatar slot hits its image
    // instead of the monogram fallback.
    const ids = [
      ...(detail.topEntries ?? []).map(e => e.profileCardId),
      ...(detail.nearbyEntries ?? []).map(e => e.profileCardId),
    ];
    await mergeMissingCardsByIds(catalog, ids);
    activeTournamentViewCleanup = createTournamentDetailView(container, detail, catalog, playerData, (action) => {
      if (action.type === 'view-ladder') {
        navigate({ kind: 'tournament-ladder', tournamentId: action.tournamentId });
        return;
      }
      handleMenuAction(action);
    });
  } catch (err) {
    console.error('Failed to load tournament', err);
    navigate({ kind: 'tournament-list' });
  }
}

async function showTournamentLadder(tournamentId: string): Promise<void> {
  hideTooltip();
  try {
    // We don't have the tournament name in this context, so pull it
    // from the cache if present. Otherwise leave the header generic;
    // the back button still works either way.
    const cached = tournamentCache.find(t => t.id === tournamentId);
    const name = cached?.displayName ?? 'Tournament';
    activeTournamentViewCleanup = await createLadderView(container, tournamentId, name, catalog, handleMenuAction);
  } catch (err) {
    console.error('Failed to load ladder', err);
    navigate({ kind: 'tournament-detail', tournamentId });
  }
}

async function showAuctionHouse(): Promise<void> {
  hideTooltip();
  const { createAuctionHouseView } = await import('./ui/AuctionHouseView.js');
  activeTournamentViewCleanup = createAuctionHouseView(
    container,
    catalog,
    playerData,
    handleMenuAction,
    (auctionId) => navigate({ kind: 'auction-detail', auctionId }),
    (gems) => {
      // Same in-place gems patch as the detail view — sweeps can
      // refund escrow (winner overage) at any point, and the list
      // endpoint's response is the client's canonical resync point.
      playerData.gems = gems;
      document.querySelectorAll<HTMLElement>('.player-gems').forEach(el => {
        el.textContent = `${gems} gems`;
      });
    },
  );
}

async function showAuctionDetail(auctionId: string): Promise<void> {
  hideTooltip();
  const { createAuctionDetailView } = await import('./ui/AuctionDetailView.js');
  activeTournamentViewCleanup = await createAuctionDetailView(
    container,
    auctionId,
    catalog,
    playerData.gems,
    () => goBack({ kind: 'auction-house' }),
    (gems) => {
      playerData.gems = gems;
      // In-place patch of the header gem readout so the balance updates
      // without a full menu re-render. Matches the pattern used after
      // gem-shop purchases.
      document.querySelectorAll<HTMLElement>('.player-gems').forEach(el => {
        el.textContent = `${gems} gems`;
      });
    },
    (cardId, art) => {
      // Mirror the server-side collection insert into the client's
      // local state so the deck editor sees the new card without a
      // page refresh. Matches the pattern used by `buyPack` / stage
      // rewards (see PlayerState.ts). The card's def is already in
      // the catalog — the auction detail view fetched it via
      // mergeMissingCardsByIds on mount.
      const now = new Date().toISOString();
      playerData.collection[cardId] = (playerData.collection[cardId] ?? 0) + 1;
      playerData.collectionAcquiredAt[cardId] = now;
      // Update the catalog's `art` URL to the cache-busted one the
      // server just wrote. Without this, the deck editor renders the
      // stale-cached `<id>.png` (24h max-age with no ETag) and shows
      // whichever variant was on disk when the browser first fetched
      // it — usually the wrong one after a claim.
      const def = catalog.get(cardId);
      if (def) def.art = art;
    },
  );
}

async function startTournamentMatch(tournamentId: string): Promise<void> {
  // Tournaments are tied to a stable player identity (rank persists
  // across sessions, prizes claim against a user_id). A guest session
  // is ephemeral — letting them join would either lose their progress
  // when the session expires or pollute the ladder with throwaway
  // entries. Gate the join with the same account-gate modal as the
  // custom-pack flow; the server also rejects guest start-match as
  // defense-in-depth.
  if (playerData.isGuest) {
    showAccountGateModal(
      'You need to sign in or create an account to join tournaments.',
      mode => showAuthModal(mode),
    );
    return;
  }

  // Resolve which of the player's decks to use. Per-tournament
  // preference (keyed by tournament DEF id, persists across cycles)
  // overrides the active deck. Falls back to active when no
  // preference is saved or the saved deck has been deleted.
  let summary = tournamentCache.find(t => t.id === tournamentId);
  // Cache miss = normal after a just-ended match (the post-game
  // route filters the played instance out to force a fresh rank
  // number on the next view render). Refetch synchronously before
  // erroring so a "play another match" tap right after game-over
  // doesn't hit a spurious "tournament unavailable" popup.
  if (!summary) {
    try {
      const resp = await apiClient.getTournaments();
      tournamentCache = resp.tournaments;
      tournamentPreviewCache = resp.previews;
      summary = tournamentCache.find(t => t.id === tournamentId);
    } catch {
      // Fall through — the guard below turns this into a clear
      // "couldn't reach the server" message via the outer catch.
    }
  }
  if (!summary) {
    showNotice({
      title: "Couldn't start match",
      message: 'The tournament is no longer available. Refresh the tournament list and try again.',
    });
    return;
  }
  const deckIndex = resolveTournamentDeckIndex(summary.defId, playerData.deckIds, playerData.activeDeckIndex);
  const tournamentDeck = playerData.decks[deckIndex];
  if (!tournamentDeck) {
    showNotice({ title: "No deck", message: 'You need at least one deck to play a tournament match.' });
    return;
  }

  // Pre-flight deck-rule check. Tournaments can override the deck
  // rule (Pauper League restricts to commons), so the chosen deck
  // might be illegal for this format even though it's a valid
  // skirmish deck. Validate before hitting the server so the player
  // sees a clear "rebuild your deck" message instead of losing on
  // turn 1 because half their cards aren't legal.
  const deckRule = rulesBundle.deckRules[summary.deckRule];
  if (deckRule) {
    const errors = validateDeckAgainstRule(tournamentDeck, catalog, deckRule);
    if (errors.length > 0) {
      showNotice({
        title: `Deck not legal for ${summary.displayName}`,
        message: errors.join(' '),
      });
      return;
    }
  }

  // Server-authoritative join: insert entry (if needed), pick
  // opponent, return their deck + match id. We then hand off to the
  // existing startGame() with the right mode and a pre-resolved
  // opponent so the AI plays the snapshotted deck instead of the
  // tiered-skirmish random pick.
  try {
    const resp = await apiClient.startTournamentMatch(tournamentId);
    activeMatchStartRank = resp.playerStartRank;
    startGame(
      {
        type: 'tournament',
        tournamentId,
        matchId: resp.matchId,
        // Snapshot rule ids from the summary NOW; the cache may be
        // replaced by a background refresh between here and match
        // start, and if the summary vanishes from cache the rules
        // must still be pauper/league/etc — not skirmish defaults.
        gameRuleId: summary.gameRule,
        deckRuleId: summary.deckRule,
      },
      resp.opponentDeck,
      resp.opponentName,
      resp.opponentProfileCardId,
      tournamentDeck,
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showNotice({ title: "Couldn't start match", message: msg });
  }
}

async function claimTournamentPrize(tournamentId: string): Promise<void> {
  try {
    // Snapshot the collection BEFORE the claim so the post-claim
    // pack-opening view can flag cards the player didn't already own.
    // The server expands `reward.pack` into concrete card ids in the
    // same transaction that records the claim, so by the time the
    // response lands the collection has already been mutated — we
    // need the pre-state to diff against.
    const preCounts = { ...playerData.collection };
    const resp = await apiClient.claimTournamentPrize(tournamentId);
    // Refresh player data so coins / gems / collection / tickets all
    // sync with the server-side state.
    playerData = await loadPlayerData();
    // Drop the prize from the cache so the next list / menu / detail
    // render reflects "no unclaimed prize".
    tournamentCache = tournamentCache.filter(t => t.id !== tournamentId);

    const cardIds = resp.cardsAwarded.map(c => c.id);
    const onModalClose = (): void => {
      // After the prize modal: if cards were granted (explicit cards
      // OR pack contents), hand off to the standard pack-opening view
      // so the player sees the reveal. Otherwise we just refresh the
      // current screen in place — the player stays on whichever view
      // they clicked Claim from (main menu, list, or detail).
      const returnHere = (): void => renderRoute(parseRoute(location.pathname));
      if (cardIds.length > 0) {
        const onAddToDeck = playerData.decks.length > 0
          ? () => navigate({ kind: 'deck-edit', index: playerData.activeDeckIndex })
          : undefined;
        showPackOpening(cardIds, returnHere, computeNewlyAcquired(preCounts, cardIds), onAddToDeck);
      } else {
        returnHere();
      }
    };
    showTournamentPrizeModal(resp.rank, resp.total, resp.reward, onModalClose);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showNotice({ title: "Couldn't claim prize", message: msg });
  }
}

/** Fancy claim modal — reuses the game-over panel's overlay / button
 *  styling and per-currency classes (`.coins-earned` for gold,
 *  `.gems-earned` for blue, `.tickets-earned` for green) so the
 *  visual language matches the per-match victory screen. The title
 *  uses the `.win` color treatment (gold) and the rank line carries
 *  the same purple accent as the in-game rank-delta line. */
function showTournamentPrizeModal(
  rank: number,
  total: number,
  reward: { coins?: number; gems?: number; cards?: string[]; pack?: { cardCount: number }; tickets?: { id: string; count: number }[] },
  onClose: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'game-over-overlay tournament-prize-overlay';

  const coins = reward.coins ?? 0;
  const gems = reward.gems ?? 0;
  // The card count surfaced on the modal is the sum of explicit
  // `cards` and the `pack`'s `cardCount` — both flow through the
  // pack-opening reveal afterwards, so showing them as one number
  // here matches what the player is about to see.
  const cardCount = (reward.cards?.length ?? 0) + (reward.pack?.cardCount ?? 0);
  const tickets = reward.tickets ?? [];
  const hasReward = coins > 0 || gems > 0 || cardCount > 0 || tickets.length > 0;

  const ticketLines = tickets
    .filter(t => t.count > 0)
    .map(t => {
      const name = ticketDisplayName(t.id);
      const label = t.count > 1 ? `${t.count}× ${name}` : name;
      return `<p class="tickets-earned">+${escapeHtmlBasic(label)}</p>`;
    }).join('');

  overlay.innerHTML = `
    <div class="game-over-panel tournament-prize-panel">
      <div class="tournament-prize-banner">🏆 Tournament Prize</div>
      <h1 class="win">Final Rank #${rank}</h1>
      <p class="subtitle">of ${total} players</p>
      ${coins > 0 ? `<p class="coins-earned">+${coins} coins</p>` : ''}
      ${gems > 0 ? `<p class="gems-earned">+${gems} gems</p>` : ''}
      ${cardCount > 0 ? `<p class="cards-earned">+${cardCount} card${cardCount === 1 ? '' : 's'}</p>` : ''}
      ${ticketLines}
      ${!hasReward ? '<p class="prize-no-reward">No prize at this placement — better luck next time!</p>' : ''}
      <div class="game-over-buttons">
        <button class="action-button" id="prize-ok-btn">Continue</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const dismiss = (): void => {
    overlay.remove();
    onClose();
  };
  overlay.querySelector('#prize-ok-btn')!.addEventListener('click', dismiss);
}

function escapeHtmlBasic(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function showCampaignList(): void {
  hideTooltip();
  // Sort by Campaign.order ascending; campaigns without an explicit order
  // fall to the end. Stable on ties so file-readdir alphabetical order is
  // preserved as a secondary key without us spelling that out.
  const all = Object.values(rulesBundle.campaigns)
    .slice()
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  createCampaignListView(container, all, playerData.beatenStageIds, handleMenuAction);
}

function showCampaignStages(campaignId: string): void {
  hideTooltip();
  const campaign = rulesBundle.campaigns[campaignId];
  if (!campaign) {
    showMainMenu();
    return;
  }
  // Kick off art preloads for every stage's named opponent before the
  // player taps in. By the time the coin-flip ceremony runs, the image is
  // typically already cached so the boss reveal slides in instantly.
  preloadCampaignOpponentArt(campaign);
  createCampaignStagesView(container, campaign, playerData.beatenStageIds, handleMenuAction);
}

function preloadCampaignOpponentArt(campaign: import('./shared/rules.js').Campaign): void {
  for (const stage of campaign.stages) {
    if (!stage.opponent) continue;
    const def = catalog.get(stage.opponent);
    if (!def?.art) continue;
    // Side-effect only: assigning to .src triggers the HTTP fetch and the
    // browser caches the response. We never read this Image again.
    const img = new Image();
    img.src = def.art;
  }
}

function showDeckBuilder(deckIndex: number): void {
  hideTooltip();
  createDeckBuilderView(container, playerData, catalog, deckIndex, (savedDeck) => {
    saveDeck(playerData, deckIndex, savedDeck).then(() => {
      goBack({ kind: 'home' });
    }).catch(err => {
      console.error('Failed to save deck:', err);
      showNotice({
        title: "Couldn't save deck",
        message: err instanceof Error ? err.message : String(err),
      });
    });
  }, () => {
    goBack({ kind: 'home' });
  }, () => {
    // Delete button. Only the deck builder surfaces it now; the
    // confirmation flow lives here so the modal can match the rest
    // of the site (showConfirm) rather than the browser-default
    // window.confirm() that previously fired from MenuView.
    if (playerData.decks.length <= 1) return;
    const deckName = playerData.decks[deckIndex]?.name ?? 'this deck';
    showConfirm({
      title: 'Delete deck',
      message: `Delete "${deckName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => {
        deleteDeck(playerData, deckIndex).then(() => {
          goBack({ kind: 'home' });
        }).catch(err => {
          console.error('Failed to delete deck:', err);
          showNotice({
            title: "Couldn't delete deck",
            message: err instanceof Error ? err.message : String(err),
          });
        });
      },
    });
  });
}

function showPackOpening(
  cardIds: string[],
  onDone: () => void,
  newCardIds?: Set<string>,
  onAddToDeck?: () => void,
): void {
  hideTooltip();
  createPackOpeningView(container, cardIds, catalog, onDone, newCardIds, onAddToDeck);
}

/** Compute which of `gainedIds` weren't in the player's collection
 *  before this acquisition. Caller snapshots `playerData.collection`
 *  (a flat `Record<cardId, count>`) immediately *before* the buy /
 *  reward call, then passes that snapshot here once the awarded ids
 *  are known. The check is "preCount > 0" rather than "key exists"
 *  because the collection map sometimes carries zero-count rows after
 *  deck edits — a 0-count row means the player has seen the card
 *  before but currently owns none. */
function computeNewlyAcquired(
  preCounts: Record<string, number>,
  gainedIds: string[],
): Set<string> {
  const out = new Set<string>();
  for (const id of gainedIds) {
    if ((preCounts[id] ?? 0) <= 0) out.add(id);
  }
  return out;
}

async function showCustomPackFlow(resumeJobId?: string): Promise<void> {
  hideTooltip();
  // Redeem a free-custom-pack ticket when the player owns one. Resume
  // flows skip the confirm screen entirely, so the ticket choice
  // only matters when we're about to render the confirm panel.
  const useFreeTicket = !resumeJobId && (playerData.tickets[CUSTOM_PACK_TICKET_ID]?.count ?? 0) > 0;
  await createCustomPackView(
    container,
    playerData.gems,
    customPackConfig,
    async (result) => {
      if (result.keptCards && result.keptCards.length) {
        // Merge into local catalog + collection so the deckbuilder sees them immediately.
        const now = new Date().toISOString();
        for (const card of result.keptCards) {
          catalog.set(card.id, card);
          playerData.collection[card.id] = (playerData.collection[card.id] ?? 0) + 1;
          playerData.collectionAcquiredAt[card.id] = now;
        }
      }
      // Refresh balances + tickets from server. `buy` may have either
      // spent gems OR redeemed a free-custom-pack ticket, and `keep`
      // moves gems for the per-card cost. Without the ticket sync,
      // the menu's FREE banner stays on after the ticket was already
      // consumed.
      try {
        const profile = await apiClient.getSession();
        if (profile) {
          playerData.gems = profile.gems;
          playerData.coins = profile.coins;
          playerData.tickets = profile.tickets ?? {};
        }
      } catch { /* ignore, next navigation refreshes */ }
      goBack({ kind: 'home' });
    },
    resumeJobId,
    () => openStoreWithLiveBalanceRefresh(),
    () => playerData.gems,
    (gems) => {
      // Keep playerData.gems in sync with the server's view after
      // each custom-pack mutation (buy and keep). Without this the
      // reveal screen's affordability getter reads the pre-pack
      // balance and never routes underfunded clicks to the store.
      playerData.gems = gems;
      document.querySelectorAll('.player-gems').forEach(el => {
        el.textContent = `${gems} gems`;
      });
    },
    useFreeTicket,
  );
}

/** Open the store and patch every visible balance element on state
 *  change. Used by views (custom-pack reveal in particular) that
 *  mustn't lose their in-progress state to a full re-render after a
 *  purchase. The DOM-walk relies on stable class conventions used by
 *  every balance display site (`.js-gem-balance`, `.player-gems`,
 *  `.player-coins`). */
function openStoreWithLiveBalanceRefresh(): void {
  showStore(storeConfig, playerData, () => {
    const gemsFormatted = `${playerData.gems} gems`;
    const coinsFormatted = `${playerData.coins} coins`;
    document.querySelectorAll('.js-gem-balance').forEach(el => {
      el.textContent = gemsFormatted;
    });
    document.querySelectorAll('.player-gems').forEach(el => {
      el.textContent = gemsFormatted;
    });
    document.querySelectorAll('.player-coins').forEach(el => {
      el.textContent = coinsFormatted;
    });
  });
}

// ── Routing ──────────────────────────────────────────────────────
//
// Top-level navigable views are mirrored into the URL so the browser back
// button rewinds in-app instead of leaving the site. Only four shapes are
// routed: home, deck-edit, campaign-list, campaign-stages. Modal-ish flows
// (pack opening, custom pack, coin-flip, auth) and forced flows (starter
// selection, in-game) intentionally don't touch the URL.

type Route =
  | { kind: 'home' }
  | { kind: 'deck-edit'; index: number }       // 0-based internally; URL is 1-based
  | { kind: 'campaign-list' }
  | { kind: 'campaign-stages'; campaignId: string }
  | { kind: 'custom-pack' }
  | { kind: 'tournament-list' }
  | { kind: 'tournament-detail'; tournamentId: string }
  | { kind: 'tournament-ladder'; tournamentId: string }
  | { kind: 'auction-house' }
  | { kind: 'auction-detail'; auctionId: string }
  | { kind: 'settings' };

function parseRoute(path: string): Route {
  const deckMatch = path.match(/^\/deck\/(\d+)\/edit\/?$/);
  if (deckMatch) return { kind: 'deck-edit', index: parseInt(deckMatch[1], 10) - 1 };
  const campMatch = path.match(/^\/campaign\/([^/]+)\/?$/);
  if (campMatch) return { kind: 'campaign-stages', campaignId: campMatch[1] };
  if (path === '/campaign' || path === '/campaign/') return { kind: 'campaign-list' };
  if (path === '/create' || path === '/create/') return { kind: 'custom-pack' };
  const ladderMatch = path.match(/^\/tournament\/([^/]+)\/ladder\/?$/);
  if (ladderMatch) return { kind: 'tournament-ladder', tournamentId: ladderMatch[1] };
  const tournMatch = path.match(/^\/tournament\/([^/]+)\/?$/);
  if (tournMatch) return { kind: 'tournament-detail', tournamentId: tournMatch[1] };
  if (path === '/tournament' || path === '/tournament/') return { kind: 'tournament-list' };
  const auctionMatch = path.match(/^\/auction\/([^/]+)\/?$/);
  if (auctionMatch) return { kind: 'auction-detail', auctionId: auctionMatch[1] };
  if (path === '/auction' || path === '/auction/') return { kind: 'auction-house' };
  if (path === '/settings' || path === '/settings/') return { kind: 'settings' };
  return { kind: 'home' };
}

function routeToPath(r: Route): string {
  switch (r.kind) {
    case 'home':              return '/';
    case 'deck-edit':         return `/deck/${r.index + 1}/edit`;
    case 'campaign-list':     return '/campaign';
    case 'campaign-stages':   return `/campaign/${r.campaignId}`;
    case 'custom-pack':       return '/create';
    case 'tournament-list':   return '/tournament';
    case 'tournament-detail': return `/tournament/${r.tournamentId}`;
    case 'tournament-ladder': return `/tournament/${r.tournamentId}/ladder`;
    case 'auction-house':     return '/auction';
    case 'auction-detail':    return `/auction/${r.auctionId}`;
    case 'settings':          return '/settings';
  }
}

function renderRoute(r: Route): void {
  // Tear down any active tournament view's countdown / observer so the
  // next render doesn't leak intervals across navigations.
  if (activeTournamentViewCleanup) {
    activeTournamentViewCleanup();
    activeTournamentViewCleanup = null;
  }
  switch (r.kind) {
    case 'home':
      showMainMenu();
      return;
    case 'deck-edit': {
      if (r.index < 0 || r.index >= playerData.decks.length) {
        // Stale or invalid deck index (e.g. deep-linked /deck/9/edit on a
        // 3-deck account). Quietly land on home with a clean URL.
        history.replaceState({ cardverse: true }, '', '/');
        showMainMenu();
        return;
      }
      showDeckBuilder(r.index);
      return;
    }
    case 'campaign-list':
      showCampaignList();
      return;
    case 'campaign-stages': {
      if (!rulesBundle.campaigns[r.campaignId]) {
        history.replaceState({ cardverse: true }, '', '/campaign');
        showCampaignList();
        return;
      }
      showCampaignStages(r.campaignId);
      return;
    }
    case 'custom-pack': {
      // Guests can't make custom cards. Quietly bounce to home rather than
      // popping a gate modal — the menu tile is the proper entry point that
      // surfaces sign-in / register choices.
      if (playerData.isGuest) {
        history.replaceState({ cardverse: true }, '', '/');
        showMainMenu();
        return;
      }
      void showCustomPackFlow();
      return;
    }
    case 'tournament-list':
      void showTournamentList();
      return;
    case 'tournament-detail':
      void showTournamentDetail(r.tournamentId);
      return;
    case 'tournament-ladder':
      void showTournamentLadder(r.tournamentId);
      return;
    case 'auction-house':
      // Guests can't access — bounce home. The tile hides for guests,
      // so this only fires on a stale deep link.
      if (playerData.isGuest) {
        history.replaceState({ cardverse: true }, '', '/');
        showMainMenu();
        return;
      }
      void showAuctionHouse();
      return;
    case 'auction-detail':
      if (playerData.isGuest) {
        history.replaceState({ cardverse: true }, '', '/');
        showMainMenu();
        return;
      }
      void showAuctionDetail(r.auctionId);
      return;
    case 'settings':
      showSettings();
      return;
  }
}

/** Forward navigation: push a new history entry tagged so we can recognize
 *  our own entries during a goBack() unwind. */
function navigate(r: Route): void {
  history.pushState({ cardverse: true }, '', routeToPath(r));
  renderRoute(r);
}

/** In-app Back-button helper. If we own the current entry, history.back()
 *  unwinds normally and the popstate listener re-renders. Otherwise the
 *  user landed directly on a deep link, so push the parent route — that way
 *  pressing browser-back later still keeps them in-site. */
function goBack(parent: Route): void {
  if ((history.state as { cardverse?: boolean } | null)?.cardverse) {
    history.back();
  } else {
    navigate(parent);
  }
}

function handleMenuAction(action: MenuAction): void {
  switch (action.type) {
    case 'choose-starter': {
      initFromStarterDeck(playerData, action.deck).then(() => {
        startGame();
      }).catch(err => {
        console.error('Failed to choose starter:', err);
        showNotice({
          title: "Couldn't set starter deck",
          message: err instanceof Error ? err.message : String(err),
        });
      });
      break;
    }
    case 'play':
      startGame();
      break;
    case 'view-campaigns': {
      // Dual-purpose action: forward from the main-menu Campaign button,
      // or backward from the campaign-stages Back button. The current URL
      // tells us which.
      if (parseRoute(location.pathname).kind === 'campaign-stages') {
        goBack({ kind: 'campaign-list' });
      } else {
        navigate({ kind: 'campaign-list' });
      }
      break;
    }
    case 'view-campaign':
      navigate({ kind: 'campaign-stages', campaignId: action.campaignId });
      break;
    case 'play-stage':
      startGame({ type: 'campaign', campaignId: action.campaignId, stageId: action.stageId });
      break;
    case 'view-settings':
      navigate({ kind: 'settings' });
      break;
    case 'view-tournaments':
      navigate({ kind: 'tournament-list' });
      break;
    case 'view-auction-house':
      navigate({ kind: 'auction-house' });
      break;
    case 'view-tournament':
      navigate({ kind: 'tournament-detail', tournamentId: action.tournamentId });
      break;
    case 'play-tournament':
      void startTournamentMatch(action.tournamentId);
      break;
    case 'claim-tournament-prize':
      void claimTournamentPrize(action.tournamentId);
      break;
    case 'back-tournament-detail':
      // From detail back to wherever the player came in from — list
      // view if they routed normally, main menu if they jumped via
      // the menu widget. `goBack` uses history.back() when the
      // current entry is ours.
      goBack({ kind: 'tournament-list' });
      break;
    case 'back-tournament-ladder':
      goBack({ kind: 'tournament-detail', tournamentId: action.tournamentId });
      break;
    case 'back-to-menu':
      goBack({ kind: 'home' });
      break;
    case 'edit-deck':
      navigate({ kind: 'deck-edit', index: action.deckIndex });
      break;
    case 'new-deck': {
      const config = getConfig();
      // Effective cap respects purchased extra slots — without this,
      // a player who just bought a slot would still hit the baseline gate.
      if (playerData.decks.length < config.maxDecks + playerData.extraDeckSlots) {
        showPrompt({
          title: 'New deck',
          message: 'What should this deck be called?',
          defaultValue: `Deck ${playerData.decks.length + 1}`,
          confirmLabel: 'Create',
          onConfirm: (name) => {
            // Create an empty deck locally, then open the builder.
            // The deckIds placeholder is assigned a real id on save.
            playerData.decks.push({ name, cards: [] });
            playerData.deckIds.push('');
            navigate({ kind: 'deck-edit', index: playerData.decks.length - 1 });
          },
        });
      }
      break;
    }
    case 'buy-deck-slot': {
      // Confirm before spending — gem purchases are irreversible and
      // a misclick on a button next to the deck list shouldn't cost
      // the player hundreds of gems. The confirm dialog opens
      // regardless of balance; affordability is checked on tap of
      // the priced button (mirrors the pack-buy → open-store flow
      // for players who're short on currency).
      const nextCost = deckSlotCosts.costs[playerData.extraDeckSlots];
      if (nextCost === undefined) break;
      showConfirm({
        title: 'Buy a deck slot?',
        message: 'Buy a slot so you can create another deck.',
        confirmLabel: `${nextCost} gems`,
        confirmVariant: 'primary-blue',
        onConfirm: () => {
          if (playerData.gems < nextCost) {
            // Reroute to the gem store so the player can top up,
            // same as the "buy pack while broke on coins" flow.
            showStore(storeConfig, playerData, () => {
              renderRoute(parseRoute(location.pathname));
            });
            return;
          }
          apiClient.buyDeckSlot()
            .then(({ newGemsBalance, extraDeckSlots }) => {
              playerData.gems = newGemsBalance;
              playerData.extraDeckSlots = extraDeckSlots;
              showMainMenu();
            })
            .catch(err => {
              showNotice({ title: "Couldn't buy slot", message: err?.message ?? String(err) });
            });
        },
      });
      break;
    }
    case 'set-active-deck': {
      setActiveDeck(playerData, action.deckIndex).then(() => {
        showMainMenu();
      }).catch(err => {
        console.error('Failed to set active deck:', err);
      });
      break;
    }
    case 'practice': {
      // Active deck (player) vs the deck the player clicked Practice
      // on (AI). Player can practice against the active deck too —
      // both sides are valid because the engine instantiates distinct
      // card instances per side regardless of whether the DeckDefs
      // are the same object. Snapshot the opponent deck so a later
      // edit doesn't reach into the in-flight match.
      const opponent = playerData.decks[action.deckIndex];
      if (!opponent) break;
      const opponentDeck: DeckDef = {
        name: opponent.name,
        cards: opponent.cards.map(c => ({ ...c })),
      };
      startGame({ type: 'practice', opponentDeck });
      break;
    }
    case 'buy-pack': {
      // Two payment paths: free with a matching ticket (priority when
      // available), otherwise spend coins. The shop tile already shows
      // FREE on the button when a ticket applies, so this branch
      // mirrors what the player sees.
      const ticketId = action.packDef.ticketId;
      const ticketCount = ticketId ? (playerData.tickets[ticketId]?.count ?? 0) : 0;
      const useTicket = ticketCount > 0 ? ticketId : undefined;
      if (useTicket || playerData.coins >= action.packDef.cost) {
        // Snapshot before the buy so we can flag newly-owned cards on
        // the reveal. buyPack mutates playerData.collection in place
        // when the response lands, so the snapshot must happen first.
        const preCounts = { ...playerData.collection };
        const onAddToDeck = playerData.decks.length > 0
          ? () => navigate({ kind: 'deck-edit', index: playerData.activeDeckIndex })
          : undefined;
        buyPack(playerData, action.packDef.id, useTicket).then(cards => {
          showPackOpening(cards, showMainMenu, computeNewlyAcquired(preCounts, cards), onAddToDeck);
        }).catch(err => {
          console.error('Failed to buy pack:', err);
        });
      }
      break;
    }
    case 'buy-custom-pack': {
      if (playerData.isGuest) {
        showAccountGateModal(
          'You need to sign in or create an account to make custom cards.',
          mode => showAuthModal(mode),
        );
      } else {
        navigate({ kind: 'custom-pack' });
      }
      break;
    }
    case 'open-store': {
      // Open the store on top of whatever screen the player is on.
      // The store's `onChange` callback re-renders the underlying
      // view so the coin/gem balance stays in sync after a purchase
      // or claim. We re-parse the current URL rather than hold a
      // `currentRoute` global — the URL is already authoritative for
      // which view is showing (every navigate / history-back update
      // touches it).
      showStore(storeConfig, playerData, () => {
        renderRoute(parseRoute(location.pathname));
      });
      break;
    }
    case 'claim-daily-gems': {
      // Direct claim from the main menu's daily-gems button. The
      // server is the gate for "is a claim available today" — the UI
      // should only render this button when canClaim is true, but if
      // the user has it stale, the 400 surfaces in the catch.
      //
      // Capture the button rect BEFORE the claim so we can anchor the
      // flyaway. The renderRoute() call below re-renders the menu,
      // which replaces the button node — so we need its position now,
      // not in the .then(). The flyaway is appended to <body> and
      // floats independently of the menu re-render.
      const btn = document.querySelector<HTMLElement>('#claim-daily-gems');
      claimDailyGems(playerData)
        .then(({ gemsGranted }) => {
          if (btn) spawnGemGainFlyaway(btn, gemsGranted);
          renderRoute(parseRoute(location.pathname));
        })
        .catch(err => console.error('Failed to claim daily gems:', err));
      break;
    }
    case 'sign-in':
      showAuthModal('sign-in');
      break;
    case 'register':
      showAuthModal('register');
      break;
    case 'sign-out': {
      if (!confirm('Sign out? Your account data is saved on the server. Sign back in any time to restore it.')) break;
      signOut().then(async newData => {
        playerData = newData;
        // Drop the previous user's custom cards from the in-memory catalog
        // — the new (guest) session has no custom cards of its own.
        catalog = await loadCardCatalog();
        await mergeCustomCardsIntoCatalog(catalog);
        showStarterSelection();
      }).catch(err => {
        console.error('Failed to sign out:', err);
        showNotice({
          title: 'Sign out failed',
          message: err instanceof Error ? err.message : String(err),
        });
      });
      break;
    }
  }
}

// ── Auth Modal ──────────────────────────────────────────────────

/** Gate modal shown when a guest tries to access a registered-only feature.
 *  Surfaces both Sign In and Create Account paths so the player picks the one
 *  that matches their state (returning user vs new user). */
function showAccountGateModal(message: string, onPick: (mode: 'sign-in' | 'register') => void): void {
  const overlay = document.createElement('div');
  overlay.className = 'auth-overlay';
  overlay.innerHTML = `
    <div class="auth-modal account-gate">
      <h2>Account Required!</h2>
      <p class="account-gate-message">${escapeHtmlSafe(message)}</p>
      <div class="auth-buttons">
        <button class="menu-btn" id="gate-cancel">Cancel</button>
        <button class="menu-btn" id="gate-sign-in">Sign In</button>
        <button class="menu-btn primary" id="gate-register">Create Account</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  function close() { overlay.remove(); }

  overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
  overlay.querySelector('#gate-cancel')!.addEventListener('click', close);
  overlay.querySelector('#gate-sign-in')!.addEventListener('click', () => { close(); onPick('sign-in'); });
  overlay.querySelector('#gate-register')!.addEventListener('click', () => { close(); onPick('register'); });
}

function escapeHtmlSafe(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function showAuthModal(mode: 'sign-in' | 'register'): void {
  const overlay = document.createElement('div');
  overlay.className = 'auth-overlay';

  const isSignIn = mode === 'sign-in';
  const title = isSignIn ? 'Sign In' : 'Create Account';
  const submitLabel = isSignIn ? 'Sign In' : 'Create Account';

  overlay.innerHTML = `
    <div class="auth-modal">
      <h2>${title}</h2>
      <input type="text" id="auth-username" placeholder="Username" autocomplete="username" />
      <input type="password" id="auth-password" placeholder="Password" autocomplete="${isSignIn ? 'current-password' : 'new-password'}" />
      <div class="auth-error" id="auth-error"></div>
      <div class="auth-buttons">
        <button class="menu-btn" id="auth-cancel">Cancel</button>
        <button class="menu-btn primary" id="auth-submit">${submitLabel}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const usernameInput = overlay.querySelector('#auth-username') as HTMLInputElement;
  const passwordInput = overlay.querySelector('#auth-password') as HTMLInputElement;
  const errorEl = overlay.querySelector('#auth-error')!;
  const submitBtn = overlay.querySelector('#auth-submit') as HTMLButtonElement;

  usernameInput.focus();

  function close() {
    overlay.remove();
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  overlay.querySelector('#auth-cancel')!.addEventListener('click', close);

  async function submit() {
    const username = usernameInput.value.trim();
    const password = passwordInput.value;

    if (!username || !password) {
      errorEl.textContent = 'Please fill in both fields.';
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = isSignIn ? 'Signing in...' : 'Creating...';
    errorEl.textContent = '';

    try {
      if (isSignIn) {
        playerData = await signIn(username, password);
        // Sign-in switches user_id, so the in-memory catalog still holds the
        // previous session's custom cards (or none, on a freshly-loaded guest).
        // Rebuild it from built-ins + the new account's custom cards.
        catalog = await loadCardCatalog();
        await mergeCustomCardsIntoCatalog(catalog);
      } else {
        await registerAccount(playerData, username, password);
      }
      close();
      if (playerData.hasChosenStarter) {
        showMainMenu();
      } else {
        showStarterSelection();
      }
    } catch (err: any) {
      errorEl.textContent = err.message || 'Something went wrong.';
      submitBtn.disabled = false;
      submitBtn.textContent = submitLabel;
    }
  }

  submitBtn.addEventListener('click', submit);
  passwordInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit();
  });
  usernameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') passwordInput.focus();
  });
}

// ── Game Logic ───────────────────────────────────────────────────

/** Walk an event list in emission order and banner every ability-triggered
 *  event sequentially. The engine emits in ring/slot order so the player
 *  sees triggers in the same order they fired. Each banner awaits its own
 *  dismiss before the next starts; players can click-skip. The function
 *  returns when the last banner has fully dismissed (or immediately if the
 *  event list contains no ability-triggered entries).
 *
 *  `clickThrough`: forwarded to playCharacterAbilityBanner so player-
 *  initiated triggers can show the banner without blocking input on the
 *  rest of the screen. See CharacterAbilityBannerOptions for details. */
async function bannerAbilityTriggers(events: GameEvent[], opts: { clickThrough?: boolean } = {}): Promise<void> {
  for (const evt of events) {
    if (evt.type !== 'ability-triggered') continue;
    const inst = state.cardInstances.get(evt.instanceId);
    if (!inst) continue;
    const def = catalog.get(inst.cardId);
    if (def) await playCharacterAbilityBanner(def, opts);
  }
}

// ── Stat-display deferral ────────────────────────────────────────
//
// When an action triggers a banner, the engine has already mutated state
// before the banner shows. A naive render exposes the new printed values
// (e.g. an ally's boosted power, a damaged enemy's reduced power, a freshly
// armored character) under the banner — so by the time the banner clears,
// the change is "old news" and the explanatory flyaway feels disconnected
// from the print update.
//
// Fix: snapshot effective power + armor before dispatch, render normally,
// then patch the affected DOM back to OLD values for the duration of the
// banner sequence. After banners complete, set the DOM to NEW values AND
// fire flyaways in the same frame.
//
// V2 covers power + armor (the user-reported cases). HP / spirit / KOs
// remain V3+.

interface StatDiff {
  instanceId: string;
  oldPower: number;
  newPower: number;
  oldArmor: number;
  newArmor: number;
  /** Was on the ring before, no longer present after — KO'd. The freeze
   *  step re-inserts the card so it stays visible behind the banner; the
   *  thaw step animates the fade-out. */
  isKO: boolean;
  /** Position fields used only when isKO=true. */
  side?: 'player' | 'opponent';
  slotIndex?: number;
  /** When isKO=true: the damage that *would have hit power* (effect
   *  intensity minus armor absorbed), summed across all combat-damage
   *  events targeting this instance. Lets the killing flyaway display the
   *  source intensity rather than clamping to the character's pre-KO
   *  power. Engine path: Verbs.damage emits combat-damage.damage = full
   *  intensity; armor absorbs first via applyDamage; the remainder is what
   *  we show. 0 means no combat-damage event hit this id (e.g. passive
   *  KO via sweepPassiveKOs) — fall back to oldPower in that case. */
  killingDamageToPower?: number;
}

interface RingStats {
  powers: Map<string, number>;
  armors: Map<string, number>;
  positions: Map<string, { side: 'player' | 'opponent'; slotIndex: number }>;
}

/** Capture every ring instance's effective power, armor, and slot position. */
function snapshotRingStats(s: GameState): RingStats {
  const powers = new Map<string, number>();
  const armors = new Map<string, number>();
  const positions = new Map<string, { side: 'player' | 'opponent'; slotIndex: number }>();
  for (const side of ['player', 'opponent'] as const) {
    const ps = getPlayerState(s, side);
    for (let i = 0; i < ps.ring.length; i++) {
      const slot = ps.ring[i];
      if (slot.instanceId) {
        powers.set(slot.instanceId, getEffectivePower(s, slot.instanceId, catalog));
        armors.set(slot.instanceId, getInstance(s, slot.instanceId).armor);
        positions.set(slot.instanceId, { side, slotIndex: i });
      }
    }
  }
  return { powers, armors, positions };
}

/** Diff before vs after. Includes:
 *   - Instances in BOTH whose power/armor changed (regular update path).
 *   - Instances in BEFORE but not AFTER (KO'd) — flagged isKO=true so the
 *     freeze can re-insert the card and the thaw can animate the fade.
 *  Freshly-played characters (in after, not before) are skipped — their
 *  displayed values are correct as-is.
 *
 *  `events` is consulted only for KO'd instances: combat-damage events
 *  targeting them carry the *full* damage intensity, which (less the
 *  armor absorbed) is the value the killing flyaway should display. */
function computeStatDiffs(before: RingStats, after: RingStats, events: GameEvent[]): StatDiff[] {
  const diffs: StatDiff[] = [];

  // Sum combat-damage intensity per defender — the engine emits one event
  // per damage instance, so multi-source overkill (rare but possible) is
  // captured by adding them up.
  const damageByDefender = new Map<string, number>();
  for (const e of events) {
    if (e.type === 'combat-damage' && e.defenderInstanceId) {
      damageByDefender.set(e.defenderInstanceId, (damageByDefender.get(e.defenderInstanceId) ?? 0) + e.damage);
    }
  }

  for (const [id, oldPower] of before.powers) {
    const newPower = after.powers.get(id);
    const oldArmor = before.armors.get(id) ?? 0;
    if (newPower === undefined) {
      const pos = before.positions.get(id);
      const totalDamage = damageByDefender.get(id) ?? 0;
      // Damage to power = full damage minus what armor absorbed. The KO'd
      // instance is gone from `after` so its newArmor is 0; absorbed = old.
      const damageToPower = totalDamage > 0 ? Math.max(0, totalDamage - oldArmor) : undefined;
      diffs.push({
        instanceId: id,
        oldPower, newPower: 0,
        oldArmor, newArmor: 0,
        isKO: true,
        side: pos?.side,
        slotIndex: pos?.slotIndex,
        killingDamageToPower: damageToPower,
      });
      continue;
    }
    const newArmor = after.armors.get(id) ?? 0;
    if (oldPower === newPower && oldArmor === newArmor) continue;
    diffs.push({ instanceId: id, oldPower, newPower, oldArmor, newArmor, isKO: false });
  }

  // Freshly-played characters (in `after`, not in `before`) usually need no
  // diff — their displayed effective power is correct as-is. EXCEPTION:
  // an on-play self-effect that changes the just-entered character's own
  // stats (e.g. Norse Skald's "boost your weakest ally" landing on himself
  // when no other ally is on the board). The first power-changed /
  // armor-changed event for that id carries the pre-effect baseline; treat
  // the freshly-played character like any other diff so the freeze / banner
  // / thaw pipeline animates the self-effect.
  for (const id of after.powers.keys()) {
    if (before.powers.has(id)) continue;
    const powerEvent = events.find(
      (e): e is GameEvent & { type: 'power-changed' } =>
        e.type === 'power-changed' && e.instanceId === id,
    );
    const armorEvent = events.find(
      (e): e is GameEvent & { type: 'armor-changed' } =>
        e.type === 'armor-changed' && e.instanceId === id,
    );
    if (!powerEvent && !armorEvent) continue;
    const newPower = after.powers.get(id)!;
    const newArmor = after.armors.get(id) ?? 0;
    const oldPower = powerEvent ? powerEvent.oldPower : newPower;
    const oldArmor = armorEvent ? armorEvent.oldArmor : newArmor;
    diffs.push({ instanceId: id, oldPower, newPower, oldArmor, newArmor, isKO: false });
  }

  return diffs;
}

/** Update / insert / remove a card's armor span to match `armor`. Mirrors
 *  the insert-or-remove pattern in updateCardElement so the conditional
 *  rendering in buildCharacterCard (no element when armor === 0) stays
 *  consistent with reality. */
function setArmorDisplay(cardEl: HTMLElement, armor: number): void {
  let armorEl = cardEl.querySelector('.card-armor') as HTMLElement | null;
  if (armor > 0) {
    if (armorEl) {
      armorEl.textContent = `🛡${armor}`;
    } else {
      const span = document.createElement('span');
      span.className = 'card-armor';
      span.textContent = `🛡${armor}`;
      const statsEl = cardEl.querySelector('.card-stats');
      const spiritEl = cardEl.querySelector('.card-spirit');
      if (statsEl && spiritEl) statsEl.insertBefore(span, spiritEl);
    }
  } else if (armorEl) {
    armorEl.remove();
  }
}

/** Patch each card's power badge + armor span back to pre-action values.
 *  For KO'd instances the card is already gone from the DOM (render
 *  removed it); we re-create the card element in its original ring slot
 *  with old power/armor so the player can see what's about to die behind
 *  the banner. The element gets a `pending-ko` marker so thaw can locate
 *  and animate it away. */
function freezeStatDisplay(diffs: StatDiff[]): void {
  for (const d of diffs) {
    if (d.isKO) {
      restoreKOCard(d);
      continue;
    }
    const cardEl = document.querySelector(`.card[data-instance-id="${d.instanceId}"]`) as HTMLElement | null;
    if (!cardEl) continue;
    if (d.oldPower !== d.newPower) {
      const badge = cardEl.querySelector('.card-power-badge');
      if (badge) badge.textContent = String(d.oldPower);
    }
    if (d.oldArmor !== d.newArmor) {
      setArmorDisplay(cardEl, d.oldArmor);
    }
  }
}

/** Re-insert a KO'd character into its original ring slot so it's visible
 *  during the banner sequence. The next render() call will clean this up
 *  (the engine state has the card in discard); we count on the thaw +
 *  animationend to handle removal earlier and gracefully. */
function restoreKOCard(d: StatDiff): void {
  if (d.side === undefined || d.slotIndex === undefined) return;
  const ringArea = document.querySelector(`.ring-area[data-side="${d.side}"]`);
  if (!ringArea) return;
  const slots = ringArea.querySelectorAll('.ring-slot');
  const slotEl = slots[d.slotIndex] as HTMLElement | undefined;
  if (!slotEl) return;
  const inst = state.cardInstances.get(d.instanceId);
  if (!inst) return;
  const def = catalog.get(inst.cardId);
  if (!def) return;
  // 4th arg overrides displayed power; 5th arg = inRing layout. The
  // instance's own currentPower / armor reflect post-damage state, so we
  // pass oldPower explicitly and patch armor after creation.
  const cardEl = createCardElement(def, inst, false, d.oldPower, true);
  cardEl.dataset.instanceId = d.instanceId;
  cardEl.classList.add('pending-ko');
  setArmorDisplay(cardEl, d.oldArmor);
  slotEl.classList.remove('empty');
  slotEl.appendChild(cardEl);
}

/** Restore post-action values + fire flyaway popups. Power deltas use
 *  heal/damage (green +N / red -N). Armor deltas use 'armor' (grey, signed).
 *  KO'd characters get power → 0 + damage flyaway + fade-out animation,
 *  all in the same frame. The element is removed when the animation ends. */
function thawStatDisplay(diffs: StatDiff[]): void {
  for (const d of diffs) {
    if (d.isKO) {
      killKOCard(d);
      continue;
    }
    const cardEl = document.querySelector(`.card[data-instance-id="${d.instanceId}"]`) as HTMLElement | null;
    if (!cardEl) continue;
    if (d.oldPower !== d.newPower) {
      const badge = cardEl.querySelector('.card-power-badge');
      if (badge) badge.textContent = String(d.newPower);
      const delta = d.newPower - d.oldPower;
      if (delta !== 0) {
        renderer.showDamagePopup(cardEl, Math.abs(delta), delta > 0 ? 'heal' : 'damage');
      }
    }
    if (d.oldArmor !== d.newArmor) {
      setArmorDisplay(cardEl, d.newArmor);
      const delta = d.newArmor - d.oldArmor;
      if (delta !== 0) {
        renderer.showDamagePopup(cardEl, delta, 'armor');
      }
    }
  }
}

/** Land the killing blow on a KO'd card placed by restoreKOCard:
 *   1. Drop the printed power to 0.
 *   2. Fire a damage flyaway. Prefer `killingDamageToPower` (the source's
 *      damage to power, post-armor) over oldPower (clamped to current
 *      power) — exposes the source intensity rather than just "the
 *      remainder it took to die." For KOs from passive sweeps where no
 *      combat-damage event targeted this id, fall back to oldPower.
 *   3. Trigger the fade-out animation.
 *  All happen in the same frame so the player sees the cause-effect chain
 *  resolve in one beat after the banner clears. */
function killKOCard(d: StatDiff): void {
  const cardEl = document.querySelector(`.card.pending-ko[data-instance-id="${d.instanceId}"]`) as HTMLElement | null;
  if (!cardEl) return;
  const badge = cardEl.querySelector('.card-power-badge');
  if (badge) badge.textContent = '0';
  const damage = d.killingDamageToPower ?? d.oldPower;
  if (damage > 0) {
    renderer.showDamagePopup(cardEl, damage, 'damage');
  }
  cardEl.classList.add('anim-ko');
  cardEl.addEventListener('animationend', () => {
    const slot = cardEl.parentElement;
    cardEl.remove();
    if (slot && slot.classList.contains('ring-slot') && !slot.querySelector('.card')) {
      slot.classList.add('empty');
    }
  }, { once: true });
}

/** Strip events that the stat-deferral system already animated so
 *  showEventPopups doesn't double-fire flyaways. Power, armor, spirit, HP,
 *  and combat-damage events all manifest as diffs already. */
function filterDeferredEvents(events: GameEvent[]): GameEvent[] {
  return events.filter(e =>
    e.type !== 'power-changed'
    && e.type !== 'armor-changed'
    && e.type !== 'spirit-changed'
    && e.type !== 'hp-changed'
    && e.type !== 'combat-damage'
  );
}

// ── Spirit-display deferral ────────────────────────────────────────
//
// Same pattern as power/armor but per-side instead of per-instance. The
// spirit-display element lives in `.player-info[data-side="X"]
// .spirit-value`; the flyaway anchors on the surrounding `.spirit-display`
// so it floats over both the icon and the value.

interface SpiritDiff {
  side: 'player' | 'opponent';
  oldSpirit: number;
  newSpirit: number;
}

function snapshotSpirits(s: GameState): Record<'player' | 'opponent', number> {
  return {
    player: getPlayerState(s, 'player').spirit,
    opponent: getPlayerState(s, 'opponent').spirit,
  };
}

function computeSpiritDiffs(
  before: Record<'player' | 'opponent', number>,
  after: Record<'player' | 'opponent', number>,
): SpiritDiff[] {
  const diffs: SpiritDiff[] = [];
  for (const side of ['player', 'opponent'] as const) {
    if (before[side] !== after[side]) {
      diffs.push({ side, oldSpirit: before[side], newSpirit: after[side] });
    }
  }
  return diffs;
}

/** Advance a pre-dispatch spirit snapshot past the action's "cost phase" so
 *  the freeze baseline reflects the cost as already paid.
 *
 *  The engine emits cost-related spirit-changed events early in dispatch:
 *    - PLAY_CHARACTER: spirit-changed (entry cost / gain) before ability-triggered.
 *    - PLAY_ACTION:    spirit-changed (cost) before action-played.
 *    - DECLARE_ATTACKERS: spirit-changed (united cost) before any ability-triggered.
 *  Effect-driven spirit changes (e.g. on-play steal-spirit, future end-of-
 *  turn spirit-gain) come *after* the first ability-triggered / action-played
 *  marker. We walk events in order, applying spirit-changed to the snapshot
 *  until we hit a marker — anything after that is an effect we still want to
 *  defer behind the banner. */
function computePostCostSpirits(
  before: Record<'player' | 'opponent', number>,
  events: GameEvent[],
): Record<'player' | 'opponent', number> {
  const advanced = { ...before };
  let effectsBegan = false;
  for (const e of events) {
    if (!effectsBegan && (e.type === 'ability-triggered' || e.type === 'action-played')) {
      effectsBegan = true;
    }
    if (!effectsBegan && e.type === 'spirit-changed') {
      advanced[e.player] = e.newSpirit;
    }
  }
  return advanced;
}

function freezeSpiritDisplay(diffs: SpiritDiff[]): void {
  for (const d of diffs) {
    const valueEl = document.querySelector(`.player-info[data-side="${d.side}"] .spirit-value`);
    if (valueEl) valueEl.textContent = String(d.oldSpirit);
  }
}

function thawSpiritDisplay(diffs: SpiritDiff[]): void {
  for (const d of diffs) {
    const displayEl = document.querySelector(`.player-info[data-side="${d.side}"] .spirit-display`) as HTMLElement | null;
    if (!displayEl) continue;
    const valueEl = displayEl.querySelector('.spirit-value');
    if (valueEl) valueEl.textContent = String(d.newSpirit);
    const delta = d.newSpirit - d.oldSpirit;
    if (delta !== 0) {
      renderer.showDamagePopup(displayEl, delta, 'spirit');
    }
  }
}

// ── HP-display deferral ────────────────────────────────────────
//
// Same pattern as spirit but for HP. The Cardverse engine emits hp-changed
// for any direct damage / leech / heal that hits a player's HP. There are
// no "HP costs" today (no mechanic where the player pays HP), so every
// hp-changed event is treated as effect-driven and deferred behind the
// banner. If a future card paid HP up-front, we'd want a cost/effect split
// like spirit has.

interface HpDiff {
  side: 'player' | 'opponent';
  oldHp: number;
  newHp: number;
}

function snapshotHps(s: GameState): Record<'player' | 'opponent', number> {
  return {
    player: getPlayerState(s, 'player').hp,
    opponent: getPlayerState(s, 'opponent').hp,
  };
}

function computeHpDiffs(
  before: Record<'player' | 'opponent', number>,
  after: Record<'player' | 'opponent', number>,
): HpDiff[] {
  const diffs: HpDiff[] = [];
  for (const side of ['player', 'opponent'] as const) {
    if (before[side] !== after[side]) {
      diffs.push({ side, oldHp: before[side], newHp: after[side] });
    }
  }
  return diffs;
}

/** Push a specific hp value into the HP bar + text. Mirrors the per-side
 *  block in BoardView's render so the displayed bar width, the .low class,
 *  and the "N / max" text stay consistent with what render would produce. */
function setHpDisplay(side: 'player' | 'opponent', hp: number): void {
  const info = document.querySelector(`.player-info[data-side="${side}"]`);
  if (!info) return;
  const hpBar = info.querySelector('.hp-bar') as HTMLElement | null;
  const hpText = info.querySelector('.hp-text') as HTMLElement | null;
  const maxHp = state.maxHp;
  if (hpBar) {
    const pct = Math.max(0, (Math.max(0, hp) / maxHp) * 100);
    hpBar.style.width = `${pct}%`;
    hpBar.classList.toggle('low', pct < 30);
  }
  if (hpText) {
    hpText.textContent = `${Math.max(0, hp)} / ${maxHp}`;
  }
}

function freezeHpDisplay(diffs: HpDiff[]): void {
  for (const d of diffs) setHpDisplay(d.side, d.oldHp);
}

function thawHpDisplay(diffs: HpDiff[]): void {
  for (const d of diffs) {
    setHpDisplay(d.side, d.newHp);
    const delta = d.newHp - d.oldHp;
    if (delta !== 0) {
      const hpContainer = document.querySelector(`.player-info[data-side="${d.side}"] .hp-bar-container`) as HTMLElement | null;
      if (hpContainer) {
        renderer.showDamagePopup(hpContainer, Math.abs(delta), delta > 0 ? 'heal' : 'damage');
      }
    }
  }
}

// ── Card draw animation ──────────────────────────────────────────
//
// New hand cards get a slide-in animation. Two flavors:
//   - "regular" draw — the engine's draw-phase fetch. Animates immediately,
//     before any banners, because it's a baseline turn step the player
//     expects (analogous to spirit-cost flyaways).
//   - "from ability" draw — anything drawn by a triggered ability (e.g.
//     "beginning of turn: draw 3"). Hidden after render via .deferred-draw,
//     then revealed with a small stagger after the banner sequence so the
//     player can see each card arrive separately.

interface DrawDiff { side: 'player' | 'opponent'; instanceId: string; }

function snapshotHandIds(s: GameState): Record<'player' | 'opponent', Set<string>> {
  return {
    player: new Set(getPlayerState(s, 'player').hand),
    opponent: new Set(getPlayerState(s, 'opponent').hand),
  };
}

function computeNewDraws(
  before: Record<'player' | 'opponent', Set<string>>,
  after: Record<'player' | 'opponent', Set<string>>,
): DrawDiff[] {
  const diffs: DrawDiff[] = [];
  for (const side of ['player', 'opponent'] as const) {
    for (const id of after[side]) {
      if (!before[side].has(id)) diffs.push({ side, instanceId: id });
    }
  }
  return diffs;
}

/** Walk the events to label each new draw as "regular" (occurred while
 *  state.phase === 'draw') or "fromAbility" (anywhere else, e.g. during
 *  an activate-phase beginning-of-turn trigger). */
function partitionDrawsByPhase(
  draws: DrawDiff[],
  events: GameEvent[],
): { regular: DrawDiff[]; fromAbility: DrawDiff[] } {
  const drawSet = new Set(draws.map(d => d.instanceId));
  const drawnInDrawPhase = new Set<string>();
  let phase: Phase | null = null;
  for (const e of events) {
    if (e.type === 'phase-changed') phase = e.phase;
    if (e.type === 'card-drawn' && phase === 'draw' && drawSet.has(e.instanceId)) {
      drawnInDrawPhase.add(e.instanceId);
    }
  }
  const regular: DrawDiff[] = [];
  const fromAbility: DrawDiff[] = [];
  for (const d of draws) {
    if (drawnInDrawPhase.has(d.instanceId)) regular.push(d);
    else fromAbility.push(d);
  }
  return { regular, fromAbility };
}

function findHandCardEl(d: DrawDiff): HTMLElement | null {
  return document.querySelector(
    `.hand-area[data-side="${d.side}"] .card[data-instance-id="${d.instanceId}"]`
  ) as HTMLElement | null;
}

/** Reveal a card via the existing .anim-draw keyframe and resolve when the
 *  animation has finished. */
function animateCardDrawIn(cardEl: HTMLElement): Promise<void> {
  return new Promise(resolve => {
    cardEl.classList.remove('deferred-draw');
    cardEl.classList.add('anim-draw');
    cardEl.addEventListener('animationend', () => {
      cardEl.classList.remove('anim-draw');
      resolve();
    }, { once: true });
  });
}

/** Hide ability-drawn cards behind .deferred-draw so they're invisible
 *  during the banner. The DOM layout is already correct from the render. */
function deferDraws(draws: DrawDiff[]): void {
  for (const d of draws) {
    const cardEl = findHandCardEl(d);
    if (cardEl) cardEl.classList.add('deferred-draw');
  }
}

/** Move ability-drawn cards to the end of their hand area's children.
 *  Engine event order is: ability draws (during activate phase) then
 *  regular draw (during draw phase), which means render places ability
 *  draws BEFORE the regular card in the hand DOM. The user-facing reading
 *  order should be regular-then-abilities (the normal turn step happens
 *  first, then the special bonus). Re-parenting via `appendChild` shifts
 *  ability cards to the right of the regular card, which already sits at
 *  the tail. Order among ability draws is preserved (insertion order). */
function reorderAbilityDrawsToEnd(abilityDraws: DrawDiff[]): void {
  for (const d of abilityDraws) {
    const handArea = document.querySelector(`.hand-area[data-side="${d.side}"]`);
    const cardEl = handArea?.querySelector(`.card[data-instance-id="${d.instanceId}"]`);
    if (cardEl && handArea) handArea.appendChild(cardEl);
  }
}

/** Animate every draw immediately and resolve when all have finished. */
async function animateDraws(draws: DrawDiff[]): Promise<void> {
  if (draws.length === 0) return;
  await Promise.all(draws.map(d => {
    const cardEl = findHandCardEl(d);
    return cardEl ? animateCardDrawIn(cardEl) : Promise.resolve();
  }));
}

/** Stagger-reveal previously deferred draws. Each card starts ~120 ms after
 *  the previous so animations overlap but the count is legible. */
async function staggerAnimateDraws(draws: DrawDiff[], staggerMs = 120): Promise<void> {
  if (draws.length === 0) return;
  await Promise.all(draws.map((d, i) => new Promise<void>(resolve => {
    setTimeout(() => {
      const cardEl = findHandCardEl(d);
      if (!cardEl) return resolve();
      animateCardDrawIn(cardEl).then(resolve);
    }, i * staggerMs);
  })));
}

/** Run the freeze / banner / animate sequence for a batch of phase-driven
 *  events. Used by SKIP_PHASE and the runAITurn auto-advance branch — both
 *  scenarios produce events that include possible end-of-turn /
 *  beginning-of-turn triggers + their draws + power changes, all from a
 *  phase advance (no action-card ceremony involved).
 *
 *  Caller must have already called renderer.render(state) so the new state
 *  is on screen before we patch values back to "before". */
async function applyDeferredPhaseEffects(
  beforeStats: RingStats,
  beforeSpirits: Record<'player' | 'opponent', number>,
  beforeHps: Record<'player' | 'opponent', number>,
  beforeHands: Record<'player' | 'opponent', Set<string>>,
  events: GameEvent[],
): Promise<void> {
  const diffs = computeStatDiffs(beforeStats, snapshotRingStats(state), events);
  const postCostSpirits = computePostCostSpirits(beforeSpirits, events);
  const finalSpirits = snapshotSpirits(state);
  const costSDiffs = computeSpiritDiffs(beforeSpirits, postCostSpirits);
  const effectSDiffs = computeSpiritDiffs(postCostSpirits, finalSpirits);
  const hpDiffs = computeHpDiffs(beforeHps, snapshotHps(state));

  const allDraws = computeNewDraws(beforeHands, snapshotHandIds(state));
  const { regular, fromAbility } = partitionDrawsByPhase(allDraws, events);
  reorderAbilityDrawsToEnd(fromAbility);
  deferDraws(fromAbility);

  freezeStatDisplay(diffs);
  freezeSpiritDisplay(effectSDiffs);
  freezeHpDisplay(hpDiffs);
  thawSpiritDisplay(costSDiffs);

  await animateDraws(regular);
  await bannerAbilityTriggers(events);

  thawStatDisplay(diffs);
  thawSpiritDisplay(effectSDiffs);
  thawHpDisplay(hpDiffs);

  // Settle delay: the thaws above fire CSS animations (KO fade ~500ms,
  // damage-popup float ~1000ms, HP bar transition) that aren't
  // awaitable from JS. Without this wait, the caller (notably the
  // SKIP_PHASE end-of-turn path that schedules `checkAITurn`) returns
  // before the visuals finish, and the AI's first action lands on top
  // of the still-fading damage flyaways from the player's EOT
  // triggers. Conditional on having actual diffs to animate so normal
  // empty-phase advances stay snappy.
  const hasVisualChange = diffs.length > 0 || effectSDiffs.length > 0 || hpDiffs.length > 0;
  if (hasVisualChange) {
    await new Promise(resolve => setTimeout(resolve, STAT_SETTLE_MS));
  }

  await staggerAnimateDraws(fromAbility);
}

/** How long to wait after thawing stat/HP/spirit displays so their CSS
 *  animations have time to play before the caller proceeds (most
 *  importantly: before the AI's turn schedule fires). Sized to cover
 *  the 500ms KO fade plus a margin; the 1s damage-popup floats over
 *  this and into the AI's setTimeout window, but is mostly faded by
 *  then. */
const STAT_SETTLE_MS = 500;

/** Pause after a non-combat lethal effect resolves, before the
 *  game-over modal / end-game interstitial takes over the screen.
 *  Longer than STAT_SETTLE_MS because this is the dramatic finale —
 *  the player should get a clear beat to see the HP hit 0 and read
 *  what killed them (especially a surprise on-play `damage-opponent`)
 *  rather than having the result screen snap up instantly. */
const GAME_OVER_DEATH_DELAY_MS = 1200;

/** Wrap `renderer.render(state)` + `applyDeferredPhaseEffects(...)` so a
 *  lethal end-/beginning-of-turn (or any phase-emitted) trigger holds the
 *  game-over modal until after its banner + flyaway play. The deferral
 *  has to fire BEFORE render — once render sees `state.winner` the modal
 *  is already on screen — so callers can't easily do this themselves
 *  without repeating the guard at every site. */
async function renderAndApplyPhaseEffects(
  beforeStats: RingStats,
  beforeSpirits: Record<'player' | 'opponent', number>,
  beforeHps: Record<'player' | 'opponent', number>,
  beforeHands: Record<'player' | 'opponent', Set<string>>,
  events: GameEvent[],
): Promise<void> {
  const willEndGame = events.some(e => e.type === 'game-over');
  if (willEndGame) renderer.deferGameOver();
  renderer.render(state);
  // Route phase-transition events to voice handling. Critical for the
  // per-turn debounce reset: `phase-changed → activate` events at
  // turn boundaries flow through `advanceUntilInteractive` and land in
  // this function's `events` argument — but never go through
  // `showEventPopups`, so without this call the per-character
  // debounce flag from a `played` line would stick around forever and
  // block that character's `combat_engage` / `kill_other` / `die`
  // bubbles on subsequent turns.
  renderer.routeVoiceLines(events);
  await applyDeferredPhaseEffects(beforeStats, beforeSpirits, beforeHps, beforeHands, events);
  if (willEndGame) await reportGameEndResult();
}

/** Advance the phase machine to the next interactive stop, split so
 *  the current turn's tail (end-of-turn triggers, KOs, HP hits)
 *  animates fully BEFORE the next player's setup phases paint. Fixes
 *  the "end-of-turn effects visibly resolve on the opponent's turn"
 *  bug: a single walk from cleanup all the way through opponent-main
 *  causes the renderer to show opponent-main first, then the EOT
 *  banners play out on top of it.
 *
 *  Pass 1 halts at the cleanup→activate boundary; pass 2 (only if
 *  we hit that boundary and the game hasn't ended) crosses it and
 *  walks to the next interactive stop. Each pass gets its own
 *  snapshot + render, so the diff animations are scoped to just the
 *  events from that pass. Caller-supplied `initialEvents` are
 *  prepended to pass 1 — used by paths (SKIP_PHASE, CONFIRM_BLOCKERS)
 *  that produce events BEFORE this helper runs and want them
 *  animated in the same batch. */
async function advanceAndRenderThroughTurn(
  initialEvents: GameEvent[] = [],
): Promise<void> {
  const before1Stats = snapshotRingStats(state);
  const before1Spirits = snapshotSpirits(state);
  const before1Hps = snapshotHps(state);
  const before1Hands = snapshotHandIds(state);

  const pass1 = advanceUntilInteractive(state, catalog, { stopAtTurnBoundary: true });
  state = pass1.state;
  const pass1Events = [...initialEvents, ...pass1.events];
  processEvents(pass1Events);
  await renderAndApplyPhaseEffects(before1Stats, before1Spirits, before1Hps, before1Hands, pass1Events);

  if (!pass1.hitBoundary || state.winner) return;

  const before2Stats = snapshotRingStats(state);
  const before2Spirits = snapshotSpirits(state);
  const before2Hps = snapshotHps(state);
  const before2Hands = snapshotHandIds(state);

  const pass2 = advanceUntilInteractive(state, catalog);
  state = pass2.state;
  processEvents(pass2.events);
  await renderAndApplyPhaseEffects(before2Stats, before2Spirits, before2Hps, before2Hands, pass2.events);
}

/** Single entry point for "the game just ended — flush the modal."
 *  For tournaments, this also posts the match report and stamps the
 *  rank delta onto the modal before it appears; for skirmish /
 *  campaign games it's a straight flush. Callers should `await` so
 *  the modal doesn't pop before the rank line is in place.
 *
 *  Idempotent: a second call is a no-op because `renderer.flushGameOver`
 *  guards on `gameOverShown`. That matters because the three game-over
 *  code paths in main.ts (auto-phase advance, declare-attackers, and
 *  standard dispatch) can all end the same match and each calls this. */
async function reportGameEndResult(): Promise<void> {
  const mode = activeGameMode;
  // Finalize the GameRecord with the current state's hash + timestamp.
  // Idempotent against the multi-fork game-over paths because state is
  // immutable by the time we land here (winner is set). The record's
  // `actions` array is the same reference as actionLog, so it already
  // reflects every action dispatched up to this point.
  if (currentGameRecord) {
    currentGameRecord.finalStateHash = hashState(state);
    currentGameRecord.recordedAt = Date.now();
  }
  // Step 1: tournament-only API report (rank delta) — needs to land
  // BEFORE the interstitial so the game-over modal (which renders after
  // the interstitial) shows the authoritative rank line.
  if (mode.type === 'tournament') {
    if (!activeMatchDeck) {
      console.warn('Tournament match ending with no active deck snapshot');
    } else {
      const won = state.winner === 'player';
      try {
        const resp = await apiClient.reportTournamentMatch(mode.tournamentId, mode.matchId, {
          won,
          deckUsed: activeMatchDeck,
          cardsPlayed: collectPlayerCardsPlayed(),
        });
        renderer.setTournamentRankChange({
          oldRank: activeMatchStartRank ?? resp.oldRank,
          newRank: resp.newRank,
          total: resp.total,
          rankUpdated: resp.rankUpdated,
          tournamentEnded: resp.tournamentEnded,
        });
        if (resp.perMatchReward) {
          loadPlayerData().then(data => { playerData = data; }).catch(() => {});
        }
      } catch (err) {
        console.error('Failed to report tournament match', err);
      }
    }
  }

  // Step 2: run the end-game interstitial. Always plays — even for
  // matches without a dossier on either card, the slide-in plays with
  // generic / fallback bubble text so the player gets the same
  // post-game beat. flushGameOver is deferred until the interstitial
  // resolves.
  await runEndGameInterstitial();
  renderer.flushGameOver();
}

/** Build the interstitial inputs from current match state and run it.
 *  Resolves when the interstitial fully plays out or is skipped. */
function runEndGameInterstitial(): Promise<void> {
  return new Promise(resolve => {
    if (!state.winner) {
      resolve();
      return;
    }
    // Look up the responsible card def. May be null for deck-out
    // (pendingEndgameResponsibleCardId === null) — the interstitial
    // renders an "Out of cards" plate in that case.
    const responsibleCard = pendingEndgameResponsibleCardId
      ? catalog.get(pendingEndgameResponsibleCardId) ?? null
      : null;
    const opponentProfileCard = activeOpponentProfileCardId
      ? catalog.get(activeOpponentProfileCardId) ?? null
      : null;
    showEndGameInterstitial({
      winner: state.winner,
      responsibleCard,
      opponentProfileCard,
      log: state.log,
      onComplete: resolve,
    });
  });
}

function handleAction(action: GameAction): void | Promise<void> {
  if (state.winner) return;
  if (state.currentPlayer !== 'player' && action.type !== 'ASSIGN_BLOCKER' && action.type !== 'UNASSIGN_BLOCKER' && action.type !== 'CONFIRM_BLOCKERS' && action.type !== 'PLAY_REACTION') return;

  // Record player actions for the action log
  if (state.currentPlayer === 'player' || action.type === 'ASSIGN_BLOCKER' || action.type === 'UNASSIGN_BLOCKER' || action.type === 'CONFIRM_BLOCKERS' || action.type === 'PLAY_REACTION') {
    actionLog.push(action);
  }

  // Return the processAction promise so callers that need to SEQUENCE
  // dispatches (notably Renderer.handleMainAction's two-step end-turn:
  // main→attack then attack→end) can await each step before issuing the
  // next. Fire-and-forget callers (drag-drop, ability buttons) simply
  // ignore the return. We must NOT gate on a "busy" flag here — that
  // would drop the second dispatch and stall the game; awaiting at the
  // call site is the correct way to order steps.
  return processAction(action);
}

async function processAction(action: GameAction): Promise<void> {
  if (state.winner) return;

  const phase = state.phase;

  if (action.type === 'SKIP_PHASE') {
    // SKIP_PHASE is the user / AI explicitly leaving the current
    // interactive phase (main → attack, attack → counter). Take that
    // step manually, then `advanceAndRenderThroughTurn` walks the
    // auto phases (end → cleanup → …) with a turn-boundary pause in
    // between so end-of-turn effects finish animating before the
    // opponent's setup phases paint.
    const initial = advancePhase(state, catalog);
    state = initial.state;
    await advanceAndRenderThroughTurn(initial.events);
    checkAITurn();
    return;
  }

  if (action.type === 'CONFIRM_BLOCKERS') {
    // Combat resolution is now a discrete step: it just resolves combat
    // (CombatResolver.resolveCombat), which mutates state, nulls
    // combatState when done, and produces combat events. State remains
    // in 'counter' phase. After the combat animation plays, any
    // post-combat phase work (end-of-turn triggers, exhaust, cleanup,
    // turn cycle, beginning-of-turn for the next player) is driven by
    // the SAME advanceUntilInteractive pipeline that SKIP_PHASE uses —
    // no duplicated paths.
    // resolveCombat clones internally — no outer clone needed.
    const combatResult = resolveCombat(state, catalog);
    state = combatResult.state;
    const combatEvents = combatResult.events;
    processEvents(combatEvents);

    // Combat is the FOURTH game-over fork: when an attack reduces HP
    // to 0, state.winner is set inside resolveCombat, and the next
    // render() would auto-show the game-over modal — racing the
    // banner sequence below AND skipping the post-game report
    // (downstream renderAndApplyPhaseEffects sees no game-over event
    // because the engine doesn't re-emit one once winner is set).
    // Defer the modal now so it stays hidden through the combat
    // animation + banner + advance-until-interactive sequence, then
    // call reportGameEndResult once at the end.
    const combatEndedGame = combatEvents.some(e => e.type === 'game-over');
    if (combatEndedGame) renderer.deferGameOver();

    // Route voice-line triggers BEFORE the combat animation runs.
    // animateCombatResolution doesn't call showEventPopups, so without
    // this explicit call the combat_engage / kill_other / die bubbles
    // would never fire from a CONFIRM_BLOCKERS dispatch. Firing here
    // (rather than after the animation completes) keeps bubbles
    // anchored to the cards in their pre-render DOM positions —
    // KO'd cards are still present until renderer.render() runs
    // post-animation, so die bubbles can find their anchor.
    //
    // The state override ensures voice-line log entries land in the
    // post-combat state's log array (the one main.ts now holds),
    // not the renderer's stale this.state from the previous render.
    renderer.routeVoiceLines(combatEvents, state);

    renderer.animateCombatResolution(combatEvents, state).then(async () => {
      renderer.render(state);

      // on-damage-opponent / passive-pierce / passive-leech triggers
      // banner alongside the combat visual. Combat-time draws (rare;
      // would only happen if a damage-opponent trigger draws) appear
      // immediately in render, not deferred — that matches combat's
      // existing per-beat animation feel.
      await bannerAbilityTriggers(combatEvents);

      // Drive the rest of the turn machine through the standard
      // two-pass pipeline: end phase + cleanup animate first (so any
      // end-of-turn triggers finish resolving), then the next player's
      // activate + draw + main. Same helper SKIP_PHASE / DISCARD_CARD
      // use so the visual "my turn ends, opponent's turn starts" beat
      // is consistent across every path.
      await advanceAndRenderThroughTurn();

      // Report + flush for combat-ended games. renderAndApplyPhaseEffects
      // won't catch this case — it gates on the phase-effect events
      // containing a game-over, but once winner is set the engine
      // doesn't re-emit one. Without this call, the deferGameOver
      // above would leave the modal hidden forever.
      if (combatEndedGame) await reportGameEndResult();

      if (!state.winner) {
        checkAITurn();
      }
    });
    return;
  }

  if (action.type === 'DECLARE_ATTACKERS') {
    // Snapshot power + armor + spirit + HP + hand IDs — on-attack triggers
    // can change these and may draw cards.
    const beforeStats = snapshotRingStats(state);
    const beforeSpirits = snapshotSpirits(state);
    const beforeHps = snapshotHps(state);
    const beforeHands = snapshotHandIds(state);

    const result = dispatch(state, action, catalog);
    state = result.state;
    processEvents(result.events);

    const counterResult = skipToCounterPhase(state, catalog);
    state = counterResult.state;
    processEvents(counterResult.events);

    // See note on `willEndGame` in the standard dispatch path below — same
    // deferral for on-attack triggers that happen to be lethal.
    const declareWillEndGame =
      result.events.some(e => e.type === 'game-over') ||
      counterResult.events.some(e => e.type === 'game-over');
    if (declareWillEndGame) renderer.deferGameOver();

    renderer.render(state);

    // Defer stat / effect-spirit display changes until after the banner
    // sequence. United-attack spirit cost is part of the cost phase and
    // fires its flyaway immediately; only effect-driven spirit changes
    // (rare from on-attack triggers) defer.
    const allEvents = [...result.events, ...counterResult.events];

    // Route voice-line triggers. DECLARE_ATTACKERS doesn't go through
    // the standard processAction → showEventPopups path; it has its
    // own branch here that previously routed engine events without
    // calling into voice routing. That meant the staging-time
    // `combat_engage` bubble (which fires off `attack-declared`)
    // never reached the renderer. Call it explicitly with the
    // combined dispatch+counter events.
    renderer.routeVoiceLines(allEvents);
    const diffs = computeStatDiffs(beforeStats, snapshotRingStats(state), allEvents);
    const postCostSpirits = computePostCostSpirits(beforeSpirits, allEvents);
    const finalSpirits = snapshotSpirits(state);
    const costSDiffs = computeSpiritDiffs(beforeSpirits, postCostSpirits);
    const effectSDiffs = computeSpiritDiffs(postCostSpirits, finalSpirits);
    const hpDiffs = computeHpDiffs(beforeHps, snapshotHps(state));
    // No "draw phase" runs during DECLARE_ATTACKERS — any draws here come
    // from on-attack triggers and are deferred behind the banner.
    const abilityDraws = computeNewDraws(beforeHands, snapshotHandIds(state));
    reorderAbilityDrawsToEnd(abilityDraws);
    deferDraws(abilityDraws);

    freezeStatDisplay(diffs);
    freezeSpiritDisplay(effectSDiffs);
    freezeHpDisplay(hpDiffs);

    // United-attack cost flyaway fires immediately.
    thawSpiritDisplay(costSDiffs);

    // Banner any on-attack / on-attack-alone / passive-attack-ready /
    // passive-cannot-block triggers that fired during DECLARE_ATTACKERS +
    // counter-phase entry. Sequence them in the order the engine emitted.
    await bannerAbilityTriggers(allEvents);

    thawStatDisplay(diffs);
    thawSpiritDisplay(effectSDiffs);
    thawHpDisplay(hpDiffs);
    await staggerAnimateDraws(abilityDraws);

    if (declareWillEndGame) await reportGameEndResult();

    if (state.currentPlayer !== 'player') {
      // Player defends. If there's nothing they can do, auto-confirm so
      // combat resolves without forcing them to click a no-op button.
      if (!canBlockAnyAttacker(state, catalog) && !hasPlayableReaction(state, catalog)) {
        setTimeout(() => processAction({ type: 'CONFIRM_BLOCKERS' }), 300);
      }
    } else {
      setTimeout(() => runAICounter(), getConfig().aiTurnDelay);
    }
    return;
  }

  // Standard action dispatch
  const beforeStats = snapshotRingStats(state);
  const beforeSpirits = snapshotSpirits(state);
  const beforeHps = snapshotHps(state);
  const beforeHands = snapshotHandIds(state);
  const result = dispatch(state, action, catalog);
  state = result.state;
  processEvents(result.events);
  renderer.onActionCommitted(action);

  // Action / reaction cards never live on the board, so the only signal
  // a play happened is whatever effect flyaways follow. Pause to ceremony
  // the card before re-rendering so the player can read what was played.
  // Spirit splits into two halves:
  //   - cost: spirit-changed events emitted BEFORE the first ability-
  //     triggered / action-played marker. Paying to play a card or its
  //     entry spirit. Apply + flyaway immediately — paying a cost should
  //     be visible the moment the player commits, before any ceremony or
  //     banner plays.
  //   - effect: spirit-changed events emitted AFTER the marker. e.g.
  //     on-play steal-spirit. Defer behind the banner so print-update +
  //     flyaway happen in the same frame as the explanation.
  const postCostSpirits = computePostCostSpirits(beforeSpirits, result.events);
  const finalSpirits = snapshotSpirits(state);
  const costSDiffs = computeSpiritDiffs(beforeSpirits, postCostSpirits);
  const effectSDiffs = computeSpiritDiffs(postCostSpirits, finalSpirits);

  // Cost flyaway fires NOW, before the action-card ceremony so the player
  // sees their SP deduct as part of dropping the card. For action cards
  // the DOM hasn't been re-rendered yet (the ceremony pause keeps the
  // played card from snapping back into the hand), so we patch the SP
  // value directly and trigger the flyaway. For characters, the SP DOM
  // is also at pre-cost (no render yet) — same patch + flyaway works
  // identically.
  thawSpiritDisplay(costSDiffs);

  // Both PLAY_ACTION and PLAY_REACTION emit `action-played`.
  const actionPlayed = result.events.find(e => e.type === 'action-played');
  if (actionPlayed) {
    // Pull the played card out of the hand DOM right away so the drag
    // controller's drop-release doesn't snap it back into the hand for
    // the duration of the ceremony. Full render after the ceremony
    // reflows the hand to its new layout.
    const playedEl = container.querySelector<HTMLElement>(
      `.card[data-instance-id="${actionPlayed.instanceId}"]`
    );
    if (playedEl) playedEl.style.display = 'none';

    const inst = getInstance(state, actionPlayed.instanceId);
    const def = catalog.get(inst.cardId);
    if (def) await playActionCardCeremony(def);
  }

  // Support consumes a hand character — show a play ceremony with the
  // supporter's art so the player gets a visible cue, especially when
  // the AI supports. Without this, support resolves silently and only
  // a downstream stat-diff hints at what happened. action-played and
  // support-attached are mutually exclusive on a single dispatch, so
  // this can't fire on top of the ceremony above.
  const supportPlayed = result.events.find(e => e.type === 'support-attached');
  if (supportPlayed) {
    const playedEl = container.querySelector<HTMLElement>(
      `.card[data-instance-id="${supportPlayed.handInstanceId}"]`
    );
    if (playedEl) playedEl.style.display = 'none';

    const inst = getInstance(state, supportPlayed.handInstanceId);
    const def = catalog.get(inst.cardId);
    // Pull the actual power bonus from the accompanying power-changed
    // event — already accounts for passive-support-bonus modifiers, so
    // a +100 base + +200 passive shows as "+300 Power" correctly.
    let bonus = 0;
    for (const ev of result.events) {
      if (ev.type === 'power-changed' && ev.instanceId === supportPlayed.ringInstanceId) {
        bonus = ev.amount;
        break;
      }
    }
    if (def) await playSupportCardCeremony(def, bonus);
  }

  // If this dispatch emitted game-over alongside an ability that fires
  // a banner, suppress the modal until after the banner + flyaways play.
  // Without this the game-over screen pops the instant the lethal effect
  // resolves — the player never sees what killed them. Flushed after the
  // animation sequence below.
  const willEndGame = result.events.some(e => e.type === 'game-over');
  if (willEndGame) renderer.deferGameOver();

  renderer.render(state);

  // Defer the visible power / armor / effect-spirit / HP changes until
  // after the banner sequence so print-update + flyaway happen at the
  // same instant.
  const diffs = computeStatDiffs(beforeStats, snapshotRingStats(state), result.events);
  const hpDiffs = computeHpDiffs(beforeHps, snapshotHps(state));
  freezeStatDisplay(diffs);
  freezeSpiritDisplay(effectSDiffs);
  freezeHpDisplay(hpDiffs);

  // No "draw phase" in standard dispatch — any draws are from on-play /
  // activated / action-card abilities and defer behind the banner /
  // ceremony.
  const abilityDraws = computeNewDraws(beforeHands, snapshotHandIds(state));
  reorderAbilityDrawsToEnd(abilityDraws);
  deferDraws(abilityDraws);

  // Banner every ability-triggered event in ring/emission order. Holding
  // popups until after the banners makes the causal chain readable: enter
  // → banner(s) → effect flyaways. Covers on-play, activated, and the
  // on-play passive callouts (boost-allies / intimidate / etc).
  //
  // Special case: when the player drags a character into the ring or
  // clicks Activate on one of their own characters, awaiting the banner
  // before flyaways feels laggy — they already chose the action and want
  // immediate feedback that the boost / damage / draw landed. Show the
  // banner concurrently with the thaw instead. Opponent plays/activations
  // and automatic triggers (on-attack, beginning/end-of-turn, etc.) still
  // defer flyaways behind the banner so the cause-effect chain reads
  // cleanly when the player isn't the one driving.
  //
  // PLAY_ACTION / PLAY_REACTION are deliberately excluded — those go
  // through the action-card ceremony first (the card flies center-screen
  // for ~1s) which is its own deliberate beat; chaining a non-awaited
  // banner on top would race the ceremony's own flyaway timing.
  // ACTIVATE_ABILITY / PLAY_CHARACTER don't switch turns, so checking
  // state.currentPlayer post-dispatch is the same as pre-dispatch.
  const playerInitiated =
    state.currentPlayer === 'player'
    && (action.type === 'ACTIVATE_ABILITY' || action.type === 'PLAY_CHARACTER');
  // For player-initiated triggers we ALSO make the banner overlay
  // click-through. The earlier change made the banner race the thaw
  // (so flyaways fire immediately) but the overlay was still
  // absorbing clicks for its full duration, leaving the game feeling
  // laggy if the player wanted to chain a follow-up action. With
  // click-through on, the overlay doesn't intercept pointer events;
  // the banner box itself remains tappable for an explicit dismiss.
  const bannerPromise = bannerAbilityTriggers(result.events, { clickThrough: playerInitiated });
  if (!playerInitiated) await bannerPromise;

  thawStatDisplay(diffs);
  thawSpiritDisplay(effectSDiffs);
  thawHpDisplay(hpDiffs);
  await staggerAnimateDraws(abilityDraws);

  // Skip everything the diff system already animated. Nothing left in
  // showEventPopups handles today, but if we add new event types they'd
  // flow through here unless explicitly filtered.
  renderer.showEventPopups(filterDeferredEvents(result.events));

  if (willEndGame) {
    // Even on the player-initiated path the banner is fire-and-forget
    // (it races the thaw for snappier feedback). For game-over we have
    // to wait — the modal must come last, otherwise it pops over the
    // banner mid-animation.
    await bannerPromise;
    // Hold a beat after the lethal effect resolves so the killing blow
    // is legible before the modal/interstitial covers the board. This
    // path handles non-combat lethal effects — most notably an
    // opponent's on-play `damage-opponent` that drops the player to 0
    // HP. The HP drain + damage flyaway kicked off in the thaw above
    // are fire-and-forget CSS animations, so without this wait the
    // game-over screen appears the instant the HP hits 0 and the
    // player never sees why. Combat deaths don't need it (they pause
    // on animateCombatResolution); EOT deaths get their own settle
    // delay in applyDeferredPhaseEffects.
    await new Promise(resolve => setTimeout(resolve, GAME_OVER_DEATH_DELAY_MS));
    await reportGameEndResult();
  }

  if (action.type === 'DISCARD_CARD') {
    // Only advance the turn if we actually reached cleanup phase — a
    // mid-turn discard (player dragged to trash from main phase to get
    // back under hand limit) should just stay in main.
    if (state.phase === 'cleanup') {
      const ps = getPlayerState(state, state.currentPlayer);
      const maxHand = getMaxHandSize(state, state.currentPlayer, catalog);
      if (ps.hand.length <= maxHand) {
        // Cross the turn boundary via the shared two-pass helper.
        // EOT triggers already fired during the earlier end phase
        // (before cleanup), so pass 1 is typically a no-op boundary
        // hit; pass 2 walks into the next player's activate/draw.
        await advanceAndRenderThroughTurn();
        checkAITurn();
      } else if (state.currentPlayer === 'opponent') {
        // AI needs to keep discarding
        checkAITurn();
      }
    }
    return;
  }

  if (action.type === 'ASSIGN_BLOCKER' || action.type === 'UNASSIGN_BLOCKER' || action.type === 'PLAY_REACTION') {
    // Don't auto-advance
  }
}

function processEvents(events: GameEvent[]): void {
  for (const event of events) {
    if (event.type === 'game-over') {
      state.log.push(event.winner === 'player' ? 'You win!' : 'You lose!');
      // Captured here so `reportGameEndResult` can find the right
      // responsible-card def when it runs the end-game interstitial.
      // The game-over event itself is consumed by the renderer's modal
      // path; nothing else holds onto its responsibleCardId.
      pendingEndgameResponsibleCardId = event.responsibleCardId;
    }
  }
}

function onGameOver(
  winner: import('./types.js').PlayerSide,
  action: import('./ui/Renderer.js').GameOverAction = 'continue',
): void {
  const isWin = winner === 'player';
  const mode = activeGameMode;

  // Tournament mode: the match was already reported in
  // reportGameEndResult(); the modal dismissal just navigates back to
  // wherever the player entered the match from (main menu, tournament
  // list, or tournament detail). The in-game UI doesn't touch the URL
  // so location.pathname still reflects the entry route; parseRoute
  // gives us the right return target. Tournament drops the action-log
  // → /game/report path entirely.
  // Practice short-circuit: no reward, no game_results row, no
  // affinity bump. The server never hears about the match. Just
  // navigate back to the main menu so the player can pick another
  // deck to test or jump into a real match.
  if (mode.type === 'practice') {
    if (action === 'claim-pack') {
      // Practice doesn't grant packs, but the modal's "Claim Pack"
      // button can still be configured to route the player to the
      // shop. Keep the same destination for muscle-memory consistency
      // with skirmish.
      navigate({ kind: 'home' });
    } else {
      renderRoute({ kind: 'home' });
    }
    return;
  }

  if (mode.type === 'tournament') {
    const tid = mode.tournamentId;
    // Invalidate the cache so showTournamentList / showMainMenu /
    // showTournamentDetail refetch the latest rank.
    tournamentCache = tournamentCache.filter(t => t.id !== tid);
    const entryRoute = parseRoute(location.pathname);
    // Fallback to the tournament detail for any unexpected route
    // (defense — only home, tournament-list, and tournament-detail
    // can launch a match today).
    const returnRoute: Route =
      entryRoute.kind === 'home'
        || entryRoute.kind === 'tournament-list'
        || (entryRoute.kind === 'tournament-detail' && entryRoute.tournamentId === tid)
        ? entryRoute
        : { kind: 'tournament-detail', tournamentId: tid };
    renderRoute(returnRoute);
    return;
  }

  // Campaign battles return to the stage list afterwards (so the player sees
  // the just-earned checkmark and the next stage unlocking). Intro stays on
  // main-menu — its hidden campaign UI doesn't have a stage list to show.
  // Skirmishes return to main menu.
  // The URL was already set when the player navigated into this view (e.g.
  // /campaign/caesar before tapping a stage tile, or / before Play). The
  // game itself doesn't change the URL, so re-rendering the matching route
  // lands them back exactly where they came from without growing history.
  //
  // "Claim Pack" overrides the default route — the player asked to go to
  // the shop, so we land them on the main menu regardless of where they
  // came from. The pack tile's FREE banner is what they're after.
  const returnToView: () => void = action === 'claim-pack'
    ? () => navigate({ kind: 'home' })
    : (mode.type === 'campaign' && mode.campaignId !== 'intro')
      ? () => renderRoute({ kind: 'campaign-stages', campaignId: mode.campaignId })
      : () => renderRoute({ kind: 'home' });
  // Snapshot before reportGame mutates playerData.collection so the
  // post-game pack-opening reveal can flag any award the player didn't
  // already own.
  const preCounts = { ...playerData.collection };
  const cardsPlayed = collectPlayerCardsPlayed();
  reportGame(playerData, isWin, mode, actionLog, cardsPlayed).then(({ cardsAwarded }) => {
    if (cardsAwarded.length > 0) {
      const ids = cardsAwarded.map(c => c.id);
      // The pack-opening view's "Add to Deck" jumps straight to the
      // active deck so the player can slot the new cards in without
      // backtracking through the menu. Hidden when there's no deck to
      // edit (degenerate state — possible only if the player deleted
      // every deck from the deck builder).
      const onAddToDeck = playerData.decks.length > 0
        ? () => navigate({ kind: 'deck-edit', index: playerData.activeDeckIndex })
        : undefined;
      showPackOpening(ids, returnToView, computeNewlyAcquired(preCounts, ids), onAddToDeck);
    } else {
      returnToView();
    }
  }).catch(err => {
    console.error('Failed to report game:', err);
    returnToView();
  });
}

// Dedup latch: at most one runAITurn may be pending OR executing at a
// time. Without this, two independent schedulers can both queue a
// runAITurn for the same game state and — now that the search is async
// and yields to the event loop BEFORE dispatching — both pick and apply
// the same action (e.g. a support gets its +Power and log line twice).
// The classic trigger: the End Turn button fires two fire-and-forget
// SKIP_PHASE dispatches; their deferred checkAITurn() calls both observe
// the opponent's turn (the second SKIP already crossed the boundary in
// shared module state) and each schedule a turn. The race is timing-
// dependent — it surfaces reliably on slower devices where the search
// stays in flight long enough for the duplicate timer to land. Latch is
// cleared the moment runAITurn begins executing, so the AI's own
// end-of-step rescheduling still chains normally.
let aiTurnPending = false;

function checkAITurn(): void {
  if (state.winner) return;
  if (state.currentPlayer !== 'opponent') return;
  if (aiTurnPending) return;
  aiTurnPending = true;
  setTimeout(() => {
    aiTurnPending = false;
    void runAITurn();
  }, getConfig().aiTurnDelay);
}

async function runAITurn(): Promise<void> {
  if (state.winner || state.currentPlayer !== 'opponent') return;

  const phase = state.phase;
  if (phase === 'counter') return;

  if (phase === 'cleanup') {
    const action = await pickOpponentAction();
    if (action && action.type === 'DISCARD_CARD') {
      actionLog.push(action);
      await processAction(action);
      renderer.render(state);
    }
    return;
  }

  if (phase === 'main') {
    const action = await pickOpponentAction();
    if (action && action.type !== 'SKIP_PHASE') {
      actionLog.push(action);
      // Await so the AI's next step (and its setTimeout) doesn't fire
      // mid-ceremony — otherwise the next play would stack on top of the
      // current one's animation.
      await processAction(action);
      renderer.render(state);
      if (state.phase === 'main' && state.currentPlayer === 'opponent') {
        checkAITurn();
      }
      return;
    }
    await processAction({ type: 'SKIP_PHASE' });
    renderer.render(state);
    return;
  }

  if (phase === 'attack') {
    const action = await pickOpponentAction();
    if (action) {
      actionLog.push(action);
      await processAction(action);
      renderer.render(state);
    }
    return;
  }

  if (state.currentPlayer === 'opponent' && !state.winner) {
    // Auto-advance the AI's phase pipeline with a turn-boundary
    // pause: opponent's end-of-turn triggers animate fully before
    // the player's beginning-of-turn / draw phase paints — the
    // mirror-image case of the player-side flow in SKIP_PHASE /
    // DISCARD_CARD / CONFIRM_BLOCKERS.
    await advanceAndRenderThroughTurn();
    if (state.currentPlayer === 'opponent' && !state.winner && state.phase !== 'counter') {
      checkAITurn();
    }
  }
}

async function runAICounter(): Promise<void> {
  if (state.winner || state.phase !== 'counter') return;

  const action = await pickOpponentAction();
  if (action && action.type !== 'CONFIRM_BLOCKERS') {
    actionLog.push(action);
    // Await so a reaction-card ceremony plays in full before the next
    // counter step (which might itself be another reaction).
    await processAction(action);
    renderer.render(state);
    setTimeout(() => runAICounter(), getConfig().aiTurnDelay / 2);
    return;
  }

  renderer.showCombatPreview(state);
  setTimeout(() => {
    processAction({ type: 'CONFIRM_BLOCKERS' });
  }, getConfig().combatPreviewDelay);
}

/** Ensure every supplied card ID is in the local catalog. Used before
 *  building the engine's initial state — `createInitialState` looks up
 *  each deck card via the catalog and silently drops anything missing,
 *  which would leave a tournament opponent with a partial deck (e.g.
 *  8 cards instead of 50) and no profile portrait when their deck
 *  includes custom cards the local client has never seen.
 *
 *  Only GUID-shaped (custom) IDs trigger a fetch — premade slugs are
 *  bundled into the catalog at app start and never go missing.
 *  Resolves silently when nothing needs fetching. Throws when the
 *  fetch times out OR when the server didn't return defs for some of
 *  the requested IDs (caller shows the user-facing error). */
async function ensureMatchCardsAvailable(
  cardIds: Array<string | null | undefined>,
  timeoutMs = 8000,
): Promise<void> {
  const wanted = new Set<string>();
  for (const id of cardIds) {
    if (typeof id !== 'string' || id.length === 0) continue;
    if (catalog.has(id)) continue;
    if (!/^[0-9a-f]{32}$/.test(id)) continue;
    wanted.add(id);
  }
  if (wanted.size === 0) return;

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('timeout')), timeoutMs);
  });
  try {
    await Promise.race([
      mergeMissingCardsByIds(catalog, Array.from(wanted)),
      timeoutPromise,
    ]);
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }

  // mergeMissingCardsByIds swallows network errors (logs a warning
  // and moves on); verify each requested ID actually landed in the
  // catalog before reporting success.
  const stillMissing = Array.from(wanted).filter(id => !catalog.has(id));
  if (stillMissing.length > 0) {
    throw new Error(`Missing custom cards: ${stillMissing.length}`);
  }
}

function startGame(
  modeOverride?: GameReportMode,
  tournamentOpponentDeck?: DeckDef,
  tournamentOpponentName?: string,
  tournamentOpponentProfileCardId?: string | null,
  /** Overrides which of the player's decks is used in the match.
   *  Currently set by the tournament flow when the player has a
   *  saved per-tournament deck preference. When omitted, the
   *  player's active deck is used (same as before). */
  playerDeckOverride?: DeckDef,
): void {
  hideTooltip();
  const activeDeck = playerDeckOverride ?? getActiveDeck(playerData);
  if (!activeDeck) return;

  // Refuse to start if the deck references cards the player no longer
  // owns (auctioned, admin-removed, etc.). Ownership-based rather than
  // any server flag so it catches every path that removes cards from
  // the collection. Steer the player to the deck editor where they can
  // replace the missing copies — save re-validates and re-enables play.
  const playable = checkDeckPlayable(activeDeck, playerData.collection);
  if (!playable.ok) {
    const idx = playerData.decks.indexOf(activeDeck);
    // Missing cards may not be in the local catalog after a page
    // refresh (auctioned cards drop out of /api/custom-cards/my).
    // Fetch defs on demand so the notice shows names, never raw GUIDs.
    void (async () => {
      await mergeMissingCardsByIds(catalog, playable.missing);
      const names = playable.missing
        .map(id => catalog.get(id)?.name)
        .filter((n): n is string => !!n);
      const namesText = names.length > 0
        ? names.join(', ')
        : 'some of the cards';
      showNotice({
        title: 'Deck needs editing',
        message: `Your deck contains cards you no longer own: ${namesText}. Edit the deck to replace them before playing.`,
        buttonLabel: idx >= 0 ? 'Edit deck' : 'OK',
        onDismissed: () => {
          if (idx >= 0) navigate({ kind: 'deck-edit', index: idx });
        },
      });
    })();
    return;
  }

  // Explicit override (e.g. from a campaign-stage tap) wins over the
  // intro-vs-skirmish auto-pick. chooseGameMode() only handles the Play
  // button and starter-deck flow.
  const previousMode = activeGameMode;
  activeGameMode = modeOverride ?? chooseGameMode();
  activeMatchDeck = activeDeck;
  const { gameRule } = resolveGameModeRules(activeGameMode);

  // Pick the AI deck. Tournament mode uses the server-provided
  // opponent snapshot (passed in); practice mode carries the player's
  // chosen opponent deck inline on the mode; campaign stages with an
  // explicit `opponentDeck` use that fixed matchup (preloaded at
  // init); otherwise fall back to the tiered-by-record randomizer
  // used for skirmishes.
  const practiceOpponentDeck = activeGameMode.type === 'practice' ? activeGameMode.opponentDeck : undefined;
  const aiDeck = tournamentOpponentDeck ?? practiceOpponentDeck ?? resolveAiDeck(activeDeck.name);

  // Pick the AI controller for this match. resolveProfileForMatch reads
  // _assignments.json (loaded at init); null = BasicAI. Logged so dev
  // can verify the right tier is being applied in mixed-mode sessions.
  activeSearchProfile = resolveProfileForMatch(activeGameMode, aiDeck.name);
  if (activeSearchProfile) {
    console.log(`[ai] match controller: SearchAI(${activeSearchProfile.id}, depth=${activeSearchProfile.search.maxDepth}, samples=${activeSearchProfile.determinization?.samples ?? 0})`);
  } else {
    console.log(`[ai] match controller: BasicAI`);
  }

  // The "actually start the match" path — clears the underlying screen,
  // builds the initial game state, runs the opening ceremony, then hands
  // control to the player. Deferred behind the intro modal (when one
  // exists) so a Cancel from that modal is a true no-op: nothing visible
  // has changed and no game state was created.
  const startMatch = async (): Promise<void> => {
    // Pre-fetch any opponent custom card defs that aren't yet in the
    // catalog. Without this, createInitialState below silently drops
    // unknown card IDs from the opponent's deck (the deck builder
    // can't materialize a card it doesn't have a def for), leaving
    // the opponent with a smaller deck than the snapshot intends —
    // and a missing profile portrait if the profile is a custom card
    // we don't own. Fetched BEFORE clearing the container so the
    // player stays on the previous view if the fetch fails. Only
    // the card *defs* are fetched here; images load lazily as cards
    // hit the DOM (they're not needed for state construction).
    try {
      await ensureMatchCardsAvailable([
        ...aiDeck.cards.map(c => c.id),
        tournamentOpponentProfileCardId,
      ]);
    } catch (err) {
      // Network hiccup / offline / server down — expected during
      // normal play, so use the styled notice modal rather than the
      // native alert (which reads as a developer error). Restore the
      // prior mode — nothing visible has changed yet, so the player
      // stays exactly where they were after they dismiss.
      activeGameMode = previousMode;
      showNotice({
        title: 'Connection problem',
        message: "Couldn't download custom card data. Check your connection and try again.",
      });
      return;
    }

    actionLog = [];
    container.innerHTML = '';
    // Seed is captured before createInitialState so we can record it. The
    // replay tool will pass the same seed back through createInitialState to
    // get a byte-identical starting state.
    const matchSeed = generateSeed();
    state = createInitialState(activeDeck, aiDeck, catalog, gameRule, matchSeed);
    currentGameRecord = {
      version: 1,
      seed: matchSeed,
      rule: gameRule,
      playerDeck: activeDeck,
      opponentDeck: aiDeck,
      actions: actionLog,
      finalStateHash: '',
      recordedAt: 0,
    };

    // Resolve the opponent's full identity (cardId + name + art) in
    // a single pass so the coin-flip portrait and the dossier wiring
    // see the same card. For skirmish this is a random deck pick that
    // we then remember for the rest of the match (drives the upper-
    // left avatar + opponent_intro / win / lose / surprised / gloat
    // bubbles + the interstitial opponent slide-in).
    const opponentIdentity = resolveOpponentIdentity(
      aiDeck,
      tournamentOpponentName,
      tournamentOpponentProfileCardId,
    );
    activeOpponentProfileCardId = opponentIdentity?.cardId ?? null;
    activePlayerProfileCardId = resolvePlayerProfileCardId();
    pendingEndgameResponsibleCardId = null;
    // No per-match reset for the voice-line pool — keeping the seen-
    // line set across matches prevents the same character from
    // repeating an end-of-battle line in back-to-back wins/losses.
    // The set holds only string references that already exist in
    // the dossier cache, so it stays small. Reloading the page is
    // the natural reset point.

    // Prefetch dossiers (fire-and-forget — bubbles silently no-op
    // until the cache populates). The prefetch covers every card the
    // match could speak: both profile cards, every card in the
    // player's deck, every card in the AI deck. Custom cards
    // referenced by the opponent that aren't in the local catalog
    // would need a separate merge — handled implicitly because the
    // VoiceBubble path looks up by cardId regardless of catalog
    // presence; if the card def is missing, we'd render the bubble
    // but couldn't anchor it (the card wouldn't have a DOM element).
    const playerDeckIds = activeDeck.cards.map(c => c.id);
    const aiDeckIds = aiDeck.cards.map(c => c.id);
    // Two-step refresh: ask the server for any dossiers that became
    // ready since the last match (one HTTP call) and evict the
    // affected cache entries, then prefetch the deck. Without this
    // the cache holds onto stale `null`s from the prior match start,
    // and a dossier produced mid-session (e.g. by the affinity gate
    // after a recent win) wouldn't surface until a page reload.
    void refreshDossierCache().then(() => prefetchDossiers([
      activeOpponentProfileCardId,
      activePlayerProfileCardId,
      ...playerDeckIds,
      ...aiDeckIds,
    ]));

    // Opening ceremony before play begins. When the rule's `startingPlayer`
    // is null the coin actually decides — show the spin. Otherwise the
    // result is pre-determined by the rule, and a spinning coin just
    // misleads the player about whether there's randomness involved; skip
    // straight to the announce banner. Campaign battles with a named
    // opponent get a fighting-game-style boss reveal in either mode.
    const coinMode = gameRule.startingPlayer === null ? 'flip' : 'announce';
    const playerPortrait = resolvePlayerPortrait();
    const opponentPortrait = opponentIdentity
      ? { name: opponentIdentity.name, art: opponentIdentity.art }
      : null;
    showCoinFlip(state.firstPlayer, playerPortrait, opponentPortrait, coinMode, () => {
      // State starts in the first player's 'activate' phase. Walk through
      // activate → draw → main so the player lands at the main-phase
      // decision point. (First-player-first-turn skips draw per rule, so
      // no card-drawn event fires here; the renderer hasn't been built
      // yet either, which would block running the draw-animation pipeline.)
      const result = advanceUntilInteractive(state, catalog);
      state = result.state;
      processEvents(result.events);

      renderer = new Renderer(container, catalog, handleAction, onGameOver, getRewardPreview(activeGameMode));
      renderer.setProfileIdentities(activeOpponentProfileCardId, activePlayerProfileCardId);
      renderer.render(state);

      // Fire the opponent_intro voice bubble from the upper-left
      // header icon. Deferred slightly so it lands after the coin-
      // flip overlay finishes its own fade-out instead of stacking on
      // top of it.
      fireOpponentIntroBubble();

      if (state.currentPlayer === 'opponent') {
        // Opponent goes first — kick off their AI turn.
        checkAITurn();
      }
    });
  };

  const cancelMatch = (): void => {
    // Restore the prior game-mode. activeGameMode is the only global
    // we touched before the modal appeared (state / actionLog / container
    // are deferred into startMatch); reverting it lets reportGame and
    // friends behave correctly if the player goes on to start a different
    // match without the page having been reloaded.
    activeGameMode = previousMode;
  };

  const intro = getStageIntro();
  if (intro) {
    // showStageIntro's confirm callback can swallow the returned
    // promise; startMatch handles its own errors via the alert.
    showStageIntro(intro.name, intro.message, intro.conditions, () => { void startMatch(); }, cancelMatch);
  } else {
    void startMatch();
  }
}

/** Look up the active campaign stage's intro briefing, if any. Returns
 *  null for skirmishes and for stages with neither a prose `introMessage`
 *  nor a non-empty `missionConditions` list — those stages have nothing
 *  worth pausing the player on. */
function getStageIntro(): { name: string; message: string | null; conditions: string[] } | null {
  const mode = activeGameMode;
  if (mode.type !== 'campaign') return null;
  const camp = rulesBundle.campaigns[mode.campaignId];
  const stage = camp?.stages.find(s => s.id === mode.stageId);
  if (!stage) return null;
  const conditions = stage.missionConditions ?? [];
  if (!stage.introMessage && conditions.length === 0) return null;
  return { name: stage.name, message: stage.introMessage, conditions };
}

/** Player-side portrait for the pre-battle ceremony. Resolution rules:
 *    - Campaign: stage.protagonist ?? campaign.protagonist. The historical
 *      figure the player is roleplaying as — Caesar's Wars defaults to
 *      Julius Caesar, Genpei to Tomoe Gozen. Per-stage override exists
 *      for multi-era POV swaps. Caption uses the CARD'S name because
 *      campaigns are proxy battles for that character.
 *    - Tournament / skirmish: art comes from the player's chosen
 *      profile card on the settings panel, but the caption is the
 *      player's USERNAME — symmetric with the opponent ("you vs
 *      OpponentName"). Mirrors how the opponent's tournament caption
 *      uses their ladder name, not their card's name. Guests with
 *      no username fall back to "You". */
function resolvePlayerPortrait(): { name: string; art: string } | null {
  const mode = activeGameMode;
  if (mode.type === 'campaign') {
    const camp = rulesBundle.campaigns[mode.campaignId];
    const stage = camp?.stages.find(s => s.id === mode.stageId);
    const cardId = stage?.protagonist ?? camp?.protagonist ?? null;
    if (cardId) {
      const def = catalog.get(cardId);
      if (def) return { name: def.name, art: def.art };
    }
    // No campaign-defined protagonist (intro stage doesn't pin one —
    // the player picks a starter and IS the protagonist). Fall through
    // to the player's profile card.
  }
  // Skirmish + tournament + protagonist-less campaign: art from profile
  // card, name from username.
  if (playerData.profileCardId) {
    const def = catalog.get(playerData.profileCardId);
    if (def) return { name: playerData.username || 'You', art: def.art };
  }
  // Last-resort: the marquee card of the active deck. This covers a
  // first-time player going straight from starter selection into the
  // intro battle — the server sets profile_card_id during chooseStarter,
  // but that field isn't in the response, so the client copy is still
  // null until the next profile reload. Starter decks ship with their
  // legendary as cards[0] (king-arthur / gilgamesh) by convention, which
  // is the same rule the server applies.
  const deck = getActiveDeck(playerData);
  const firstCardId = deck?.cards[0]?.id;
  if (firstCardId) {
    const def = catalog.get(firstCardId);
    if (def) return { name: playerData.username || 'You', art: def.art };
  }
  return null;
}

interface OpponentIdentity {
  cardId: string;
  name: string;
  art: string;
}

/** Resolve the opponent's identity for this match in a single pass —
 *  cardId + display name + art together — so the coin-flip portrait
 *  and the dossier/avatar wiring see the SAME card. (Previously these
 *  were two separate functions and the skirmish path diverged: the
 *  portrait picked a random card while the profile-id helper picked
 *  the first card in the deck, so the player saw one opponent on the
 *  coin flip and a different one in battle.)
 *
 *  Resolution rules:
 *    - Campaign: stage.opponent (the named boss). Display name = card
 *      name.
 *    - Tournament: the opponent's profile card resolved server-side at
 *      /start-match time. Display name = the opponent's ladder name
 *      (not the card name) so the player recognizes them from the
 *      leaderboard. Legacy bots without a profile card fall back to a
 *      random deck pick.
 *    - Skirmish: a random card from the AI deck. Random per match,
 *      remembered for the whole match. */
function resolveOpponentIdentity(
  aiDeck: DeckDef,
  tournamentName?: string,
  tournamentProfileCardId?: string | null,
): OpponentIdentity | null {
  const mode = activeGameMode;
  if (mode.type === 'campaign') {
    const camp = rulesBundle.campaigns[mode.campaignId];
    const stage = camp?.stages.find(s => s.id === mode.stageId);
    if (!stage?.opponent) return null;
    const def = catalog.get(stage.opponent);
    if (!def) return null;
    return { cardId: stage.opponent, name: def.name, art: def.art };
  }
  if (mode.type === 'tournament') {
    if (tournamentProfileCardId) {
      const def = catalog.get(tournamentProfileCardId);
      if (!def) return null;
      return {
        cardId: tournamentProfileCardId,
        name: tournamentName ?? def.name,
        art: def.art,
      };
    }
    // Legacy bot fallback: random deck pick, but keep the ladder name.
    const fallback = randomDeckIdentity(aiDeck);
    if (!fallback) return null;
    return { cardId: fallback.cardId, name: tournamentName ?? fallback.name, art: fallback.art };
  }
  // Skirmish: random pick. ONE pick that drives both the coin-flip
  // portrait and the dossier-bearing profile identity for the match.
  return randomDeckIdentity(aiDeck);
}

/** Uniformly pick one card from a deck and return its full identity
 *  (cardId + name + art). Falls back to a linear scan if the random
 *  attempts all land on cards that aren't in the catalog. Returns
 *  null only when the deck is empty or every entry is missing from
 *  the catalog (defensive — deck loader rejects empty decks). */
function randomDeckIdentity(deck: DeckDef): OpponentIdentity | null {
  if (!deck.cards || deck.cards.length === 0) return null;
  for (let attempt = 0; attempt < 6; attempt++) {
    const pick = deck.cards[Math.floor(Math.random() * deck.cards.length)];
    const def = catalog.get(pick.id);
    if (def) return { cardId: pick.id, name: def.name, art: def.art };
  }
  for (const c of deck.cards) {
    const def = catalog.get(c.id);
    if (def) return { cardId: c.id, name: def.name, art: def.art };
  }
  return null;
}

/** Profile-card identity for the player side. Mirrors
 *  `resolvePlayerPortrait`: campaign protagonist override wins,
 *  otherwise the player's settings profile card. */
function resolvePlayerProfileCardId(): string | null {
  const mode = activeGameMode;
  if (mode.type === 'campaign') {
    const camp = rulesBundle.campaigns[mode.campaignId];
    const stage = camp?.stages.find(s => s.id === mode.stageId);
    return stage?.protagonist ?? camp?.protagonist ?? null;
  }
  return playerData.profileCardId ?? null;
}

/** Fire the opponent_intro voice bubble from the upper-left header
 *  icon, if the opponent profile has a dossier with intro lines.
 *  Called from the coin-flip onComplete callback after the renderer
 *  has mounted and applied the profile icons. */
function fireOpponentIntroBubble(): void {
  if (!activeOpponentProfileCardId) return;
  const dossier = lookupDossierOrNull(activeOpponentProfileCardId);
  if (!dossier) return;
  const lines = dossier.voice_lines.opponent_intro;
  if (!lines || lines.length === 0) return;
  const line = pickVoiceLine(lines);
  if (!line) return;
  const def = catalog.get(activeOpponentProfileCardId);
  if (def) {
    logVoiceLine(state.log, def.name, line, dossier.can_speak);
    // The board was just rendered by startMatch; refresh the log
    // section so the intro entry shows up alongside "Game started!"
    // without waiting for the player's first action.
    renderer.refreshLog();
  }
  // Small delay so the bubble appears AFTER the coin-flip overlay
  // finishes its own fade-out — otherwise it stacks on top of the
  // coin animation visually.
  setTimeout(() => {
    showVoiceBubble({
      text: line,
      variant: dossier.can_speak ? 'speaker' : 'narrator',
      anchor: 'opponent-header',
    });
  }, 400);
}


function resolveAiDeck(playerDeckName: string): DeckDef {
  const mode = activeGameMode;
  if (mode.type === 'campaign') {
    const camp = rulesBundle.campaigns[mode.campaignId];
    const stage = camp.stages.find(s => s.id === mode.stageId);
    if (stage?.opponentDeck && opponentDecksByFile[stage.opponentDeck]) {
      return opponentDecksByFile[stage.opponentDeck];
    }
  }
  const score = playerData.wins - playerData.losses;
  return pickAiDeck(score, playerDeckName);
}

function getRewardPreview(mode: GameReportMode): RewardPreview {
  if (mode.type === 'practice') {
    // Practice matches have no rewards by design — the game-over
    // banner shows neutral 0s and no card-reward strip.
    return {
      winCoins: 0, winGems: 0, lossCoins: 0, lossGems: 0,
      winTickets: [], winHasCardReward: false,
    };
  }
  if (mode.type === 'skirmish') {
    const w = rulesBundle.skirmish.winReward;
    const l = rulesBundle.skirmish.lossReward;
    return {
      winCoins: w.coins ?? 0, winGems: w.gems ?? 0,
      lossCoins: l.coins ?? 0, lossGems: l.gems ?? 0,
      winTickets: w.tickets ?? [],
      winHasCardReward: !!(w.cards?.length || w.pack),
    };
  }
  if (mode.type === 'tournament') {
    // Tournament per-match prizes vary per definition and aren't
    // known to the client until /start-match returns. We leave the
    // win/loss numbers at 0 for the initial preview; the modal is
    // updated post-report via `renderer.setTournamentRankChange`
    // before being flushed, at which point the rank line carries
    // the meaningful info. The tournament's per-match coin reward
    // (typically 1–5 coins) is intentionally not double-counted
    // here.
    return {
      winCoins: 0, winGems: 0, lossCoins: 0, lossGems: 0,
      winTickets: [], winHasCardReward: false,
      tournamentRankChange: null,
    };
  }
  const camp = rulesBundle.campaigns[mode.campaignId];
  const stage = camp.stages.find(s => s.id === mode.stageId)!;
  // Mirror the server's first-win-vs-replay logic from /game/report so the
  // game-over banner doesn't promise the first-win bounty (e.g. 150 coins
  // for Alesia) when the player actually earns the smaller replayWinReward
  // (25 coins). The server is authoritative — this is just the preview.
  const alreadyBeaten = playerData.beatenStageIds.includes(stage.id);
  const winReward = (alreadyBeaten ? stage.replayWinReward : stage.firstWinReward) ?? null;
  return {
    winCoins: winReward?.coins ?? 0,
    winGems: winReward?.gems ?? 0,
    lossCoins: stage.lossReward?.coins ?? 0,
    lossGems: stage.lossReward?.gems ?? 0,
    winTickets: winReward?.tickets ?? [],
    winHasCardReward: !!(winReward?.cards?.length || winReward?.pack),
  };
}

// ── Initialization ───────────────────────────────────────────────

async function init(): Promise<void> {
  container = document.getElementById('game')!;
  container.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:var(--text-secondary)">Loading Cardverse...</div>';

  try {
    const [loadedCatalog, loadedPacks, manifest, loadedDifficulty, loadedCustomPack, loadedRules, loadedStore, loadedSlotCosts] = await Promise.all([
      loadCardCatalog(),
      loadPacksConfig(),
      loadDeckManifest(),
      loadDifficultyConfig(),
      loadCustomPackConfig(),
      apiClient.getRulesBundle(),
      loadStoreConfig(),
      loadDeckSlotCosts(),
    ]);
    catalog = loadedCatalog;
    packsConfig = loadedPacks;
    customPackConfig = loadedCustomPack;
    storeConfig = loadedStore;
    deckSlotCosts = loadedSlotCosts;
    aiDifficulty = loadedDifficulty;
    rulesBundle = loadedRules;

    decksByTier = await loadAllDecks(manifest);
    starterDecks = decksByTier['00'] ?? [];
    opponentDecksByFile = await loadCampaignOpponentDecks(rulesBundle);

    // Load AI tuning data: _assignments.json maps mode → profile id;
    // referenced profile JSONs are loaded eagerly so per-match resolution
    // (resolveProfileForMatch, called from startGame) is synchronous.
    try {
      const respAssign = await fetch('data/ai-tuning/_assignments.json');
      if (respAssign.ok) {
        aiAssignments = await respAssign.json();
        const profileIds = new Set<string>();
        for (const [key, value] of Object.entries(aiAssignments)) {
          if (key.startsWith('_')) continue;
          if (value === 'basic') continue;
          profileIds.add(value);
        }
        await loadAndResolveProfiles([...profileIds]);
        console.log(`[ai] loaded ${aiProfilesById.size} profile(s): ${[...aiProfilesById.keys()].join(', ')}`);
      }
    } catch (err) {
      console.warn('[ai] failed to load assignments / profiles:', err);
    }
    // URL override: `?ai-search=<id>` forces a specific profile across all
    // matches (use 'basic' to force BasicAI). Honored by resolveProfileForMatch.
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('ai-search');
    if (searchParam) {
      if (searchParam !== 'basic' && !aiProfilesById.has(searchParam)) {
        // Lazy-load an unreferenced profile so dev can test arbitrary
        // profiles via URL without editing _assignments.json. Resolves
        // $extends so URL-loaded profiles inherit from base too.
        try {
          await loadAndResolveProfiles([searchParam]);
        } catch { /* fall through; resolveProfileForMatch will warn */ }
      }
      aiSearchUrlOverride = searchParam;
      console.log(`[ai] URL override active: ai-search=${searchParam}`);
    }
    // Side panel for live inspection of SearchAI's per-move reasoning.
    // Gated here means production users never see it even if they
    // stumble onto the URL.
    if (urlParams.has('ai-debug')) {
      aiDebugPanel = mountAIDebugPanel(catalog);
      aiDebugPanel.update(null);
    }

    // Dev-time hook: drop into the browser console after a match and call
    // `__cardverseExportRecord()` to get a JSON blob that replay-log.ts can
    // re-run. Logs a warning if called between matches (no record yet).
    (window as unknown as { __cardverseExportRecord?: () => string | null }).__cardverseExportRecord = () => {
      if (!currentGameRecord) {
        console.warn('No active GameRecord. Start (and end) a match first.');
        return null;
      }
      const json = JSON.stringify(currentGameRecord, null, 2);
      console.log(`[cardverse] GameRecord — seed=${currentGameRecord.seed}, actions=${currentGameRecord.actions.length}, finalHash=${currentGameRecord.finalStateHash || '(pending)'}`);
      return json;
    };

    playerData = await loadPlayerData();
    await mergeCustomCardsIntoCatalog(catalog);

    // Browser back/forward replays the matching view. Registered after the
    // data dependencies (playerData, rulesBundle, catalog) are loaded so the
    // listener can rely on them.
    window.addEventListener('popstate', () => renderRoute(parseRoute(location.pathname)));

    // Browsers preserve `history.state` across page refresh. If the
    // player reloaded while a UI-only modal was open (e.g. the
    // auction-house sell picker), the current history entry still
    // carries that modal's marker AND is a duplicate of the route
    // entry below it. Just rewriting the state (`replaceState`) leaves
    // both entries on the stack — Back would pop from one /auction to
    // the other and just re-render. We need to actually consume the
    // duplicate by calling `history.back()`; popstate re-renders the
    // now-current entry (same URL), so the view looks the same but
    // the stack is one shorter, and the next Back press reaches the
    // actual previous route.
    const currentState = history.state as { auctionSellPicker?: boolean } | null;
    if (currentState?.auctionSellPicker) {
      history.back();
    }

    if (!playerData.hasChosenStarter) {
      showStarterSelection();
    } else {
      // Resume an in-flight custom pack if the tab was refreshed mid-flow.
      let resumeJobId: string | undefined;
      try {
        const resp = await apiClient.getLatestActiveCustomPack();
        if (resp.job) resumeJobId = resp.job.id;
      } catch { /* ignore */ }
      if (resumeJobId) {
        // Resuming an in-flight pack job overrides the URL — the user
        // shouldn't lose progress just because they refreshed on `/` or
        // some other route. Pin the URL to `/create` so back-button +
        // routing stay consistent with the view they're seeing.
        //
        // Use pushState (not replaceState) so the original load entry
        // is preserved underneath. Without this, goBack() at the end of
        // the pack flow calls history.back() and the user exits the
        // site entirely (back to whatever was open before cardverse).
        // With pushState, history.back() lands on the original URL,
        // which the popstate listener renders normally.
        if (parseRoute(location.pathname).kind !== 'custom-pack') {
          history.pushState({ cardverse: true }, '', '/create');
        }
        await showCustomPackFlow(resumeJobId);
      } else {
        // Honor the URL the user landed on (deep link or refresh) instead of
        // unconditionally returning to the main menu.
        renderRoute(parseRoute(location.pathname));
      }
    }
  } catch (err) {
    console.error('Failed to initialize:', err);
    container.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100%;color:var(--accent);flex-direction:column;gap:8px">
      <div>Failed to load game</div>
      <div style="font-size:12px;color:var(--text-secondary)">${err}</div>
    </div>`;
  }
}

init();
